import React, { useEffect, useState, useCallback } from "react";
import { ago, fmtCompact, fmtUsdCompact, parseApiTime } from "../../lib/format";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  FlaskConical, BookOpen, Cpu, BarChart3,
  AlertTriangle, RefreshCw, Activity, Atom, GitBranch, Shield, Heart, Eye,
  X, FileText, ChevronUp, Trophy, Zap, ExternalLink, Sparkles,
} from "lucide-react";

// ── AEON Internal Research Paper ──────────────────────────────────────────────

const AEON_PAPER = {
  title: "AEON: A Quantum-Probabilistic Autonomous Trading Intelligence",
  subtitle: "Architecture, Empirical Findings, and Adaptive Learning in Multi-Engine Cryptocurrency Markets",
  date: "2026-06-14",
  internal: true,
  relevance_score: 1.0,
  abstract_summary:
    "Multi-engine autonomous trading system for crypto derivatives. 9 engines, 16-gate signal pipeline, quantum identity framework (H, C, S_norm), 70,000-trade simulation, live paper trading diagnostic. Central finding: R:R ratio is the dominant performance lever, outweighing all signal quality improvements.",
  sections: [
    {
      heading: "Abstract",
      body: `This paper presents AEON (Autonomous Engine for Optimized Navigation), a novel multi-engine autonomous trading system designed for cryptocurrency derivatives markets. We describe the full architectural specification of AEON's nine-engine ensemble, the quantum-probabilistic mathematical identity framework governing system behavior, empirical findings from 10,000+ simulated trades per engine, live paper-trading diagnostics across a five-day window, and the iterative engineering interventions applied across thirty-plus development sessions.\n\nOur central findings are: (1) reward-to-risk ratio is the single dominant performance lever across all engine architectures, outweighing signal quality improvements; (2) cross-engine correlation during correlated market drawdowns is a systemic risk not addressable at the individual engine level; (3) a self-modifying parameter system governed by quantum health state (H) is architecturally achievable and empirically necessary for live stability; and (4) a 16-gate signal validation pipeline with Hurst exponent filtering, ensemble voting, and stress-adjusted position sizing substantially reduces capital destruction from low-quality signal environments. The system is live at aeontrading.xyz and has processed 1,568 paper trades through its validation pipeline.`,
    },
    {
      heading: "1. Introduction",
      body: `Algorithmic trading in cryptocurrency markets presents a unique combination of high liquidity, extreme volatility regimes, continuous 24/7 operation, and relatively shallow market microstructure compared to equities. These properties simultaneously make cryptocurrencies attractive for systematic trading and pathologically dangerous for naive strategy deployment. Most retail and semi-institutional algorithmic systems fail not because their signals are wrong, but because their risk sizing, leverage management, and regime awareness are insufficient to survive the fat-tail loss events that cryptocurrency markets generate with regularity.\n\nAEON was designed from the outset to address these failure modes through three core architectural principles: (1) ensemble independence — multiple signal-generating engines with heterogeneous methodologies that disagree in correlated crashes rather than amplifying them; (2) quantum-probabilistic self-assessment — a mathematical framework borrowed from quantum information theory that allows the system to measure its own health, coherence, and entropy in real time and modify behavior accordingly; and (3) adaptive gate pipelines — a multi-layer signal validation architecture that ensures no trade reaches paper or live execution without clearing regime, momentum, risk, stress, entropy, and neural consensus checks.`,
    },
    {
      heading: "2. System Architecture",
      subsections: [
        {
          label: "2.1 Technology Stack",
          body: "Backend: Python 3.12 · FastAPI · PM2 · uvicorn\nDatabase: MongoDB (motor async) · collection aeon\nFrontend: React · craco · nginx · SSL (Let's Encrypt)\nML Runtime: PyTorch 2.x · numpy · pandas (venv)\nAux Services: NEXUS port 8001 · SSH Terminal port 8002\nBackend entry server.py exceeds 4,600 lines.",
        },
        {
          label: "2.2 The Nine Trading Engines",
          table: {
            headers: ["Engine", "Min Conf", "R:R", "Max Lev", "Class"],
            rows: [
              ["Elite Strategy", "90%", "2.0", "50x", "High-selectivity confluences"],
              ["Dual Trading Engine", "82%/77%", "2.0", "35x", "Bi-modal long/short-term"],
              ["Autonomous Trader V2", "80%", "2.0", "20x", "Multi-pair systematic"],
              ["Free Will V2", "75%", "1.5", "50x", "Macro-aware momentum"],
              ["VWAP Scalper", "70%", "1.5", "150x", "Institutional microstructure"],
              ["Day Trader", "77%", "1.5", "35x", "Intraday derivatives"],
              ["YOLO Engine", "75%", "1.5", "50x", "Momentum burst detection"],
              ["Institutional Scalper", "80%", "2.0", "40x", "Smart money / order flow"],
              ["TCN Neural Engine", "75%", "1.5", "30x", "Deep learning (temporal)"],
            ],
          },
        },
        {
          label: "2.3 The 16-Gate Signal Pipeline",
          gates: [
            "Gate 1  · NEXUS architectural regime check",
            "Gate 2  · REVERSING-trend directional gate (≥85% conf for LONG)",
            "Gate 3  · H-gate block (H < 0.30 → reject ALL signals)",
            "Gate 4  · H-gate scale (H < 1.00 → size × H)",
            "Gate 5  · Team auto-caution (SIGMA ≥85% CAUTION → conf gate)",
            "Gate 6  · MTF confluence (multi-timeframe agreement)",
            "Gate 7  · Fear & Greed regime filter",
            "Gate 8  · Macro directional gate (BTC EMA20 4H/1D)",
            "Gate 9  · Entropy gate (engine ensemble diversity)",
            "Gate 10 · Hurst exponent gate (RANDOM_WALK → reject)",
            "Gate 11 · Ensemble consensus (cross-engine vote tracking)",
            "Gate 12 · Quant gatekeeper (composite score ≥ threshold)",
            "Gate 13 · ORIA Edge Filter (7d vs 30d win-rate decay)",
            "Gate 14 · ORIA Stress Monitor (exp(−2.5×S) multiplier)",
            "Gate 15 · MTF Convergence (multi-engine bonus)",
            "Gate 16 · Team late-gate (no-quant path)",
          ],
        },
      ],
    },
    {
      heading: "3. The Quantum Identity Framework",
      body: `AEON's decision-making at time t is governed by:\n\n  A(t) = Ω(|Ψ⟩, E, M, L)\n\nWhere A(t) is the trading decision, Ω is the omega synthesis operator, |Ψ⟩ is the quantum superposition of all engine signals, E is the engine performance tensor, M is the memory state, and L is the learning signal.\n\nEngine superposition:\n  |Ψ⟩ = Σᵢ αᵢ(t)|Eᵢ⟩\n\nEach amplitude:\n  αᵢ(t) = Sᵢ · e^(−λδᵢ) · 𝟙[ADX>θ] · (1 − ρᵢ_max)\n\nKelly score:\n  Sᵢ = [(WRᵢ · RRᵢ − (1−WRᵢ)) × PFᵢ] / max(δᵢ, 0.01)\n\nSystem Health:\n  H = Σ(ωᵢ · kelly_edgeᵢ) / (1 + max(δᵢ))\n  H < 0.30 → trading halt. H < 1.00 → size × H.\n  Current live value: H = 0.238 (system correctly self-halted).\n\nCoherence:\n  C = (Σαᵢ)² / (n · Σαᵢ²)  ∈ [0,1]\n\nEntropy:\n  S_norm = −Σ pᵢ log(pᵢ) / log(n)  ∈ [0,1]\n\nPosition size:\n  size(t) = BASE_SIZE_USD × C × (1 − S_norm)\n\nThe Omega Cycle runs every 6h: DETECT → DIAGNOSE → PROPOSE → SANDBOX → VALIDATE → APPLY. Accepted Lθ modifications persist to aeon_ltheta_params.py with bounds: confidence [60%,95%], leverage [5x,150x].`,
    },
    {
      heading: "4. Simulation Findings (10,000 Trades / Engine)",
      subsections: [
        {
          label: "4.1 Baseline Results (Pre-Fix)",
          table: {
            headers: ["Engine", "Win Rate", "PnL (R)", "Max DD", "Profit Factor", "Expect/Trade"],
            rows: [
              ["Elite Strategy", "67.9%", "+11,052R", "7.0R", "4.84", "+1.105R"],
              ["Dual Engine", "64.7%", "+10,089R", "8.3R", "4.18", "+1.009R"],
              ["Autonomous V2", "63.8%", "+9,843R", "8.7R", "4.02", "+0.984R"],
              ["Free Will V2", "61.4%", "+5,964R", "8.2R", "2.72", "+0.596R"],
              ["VWAP Scalper", "58.1%", "+5,146R", "9.4R", "2.36", "+0.515R"],
              ["Day Trader", "57.8%", "+5,077R", "10.5R", "2.34", "+0.508R"],
              ["YOLO Engine", "62.0%", "+2,911R", "17.8R", "1.85", "+0.291R"],
            ],
          },
        },
        {
          label: "4.2 Post-Fix Delta (All 7 Fixes Applied)",
          table: {
            headers: ["Engine", "ΔWin Rate", "ΔPnL (R)", "ΔMax DD", "ΔProfit Factor"],
            rows: [
              ["YOLO Engine", "+1.2%", "+3,469R ⭐", "−10.4R", "+1.08"],
              ["Day Trader", "+5.2%", "+1,270R", "−3.1R", "+0.57"],
              ["Dual Engine", "+1.3%", "+376R", "−0.3R", "+0.23"],
              ["Autonomous V2", "+1.2%", "+352R", "−1.1R", "+0.22"],
              ["Elite Strategy", "+1.3%", "+359R", "+3.0R", "+0.27"],
              ["Free Will V2", "+1.9%", "+476R", "0.0R", "+0.23"],
              ["VWAP Scalper", "+1.1%", "+268R", "−0.7R", "+0.11"],
            ],
          },
        },
        {
          label: "4.3 Regime Breakdown (Win Rate %)",
          table: {
            headers: ["Engine", "Bull", "Bear", "Sideways", "Sideways Drop"],
            rows: [
              ["Elite Strategy", "72.9%", "74.7%", "60.8%", "−13%"],
              ["Dual Engine", "65.1%", "67.5%", "60.2%", "−5%"],
              ["Autonomous V2", "63.3%", "67.5%", "60.2%", "−3%"],
              ["Free Will V2", "64.5%", "59.0%", "60.8%", "−4%"],
              ["VWAP Scalper", "55.4%", "60.2%", "57.2%", "mixed"],
              ["Day Trader", "54.2%", "58.4%", "54.2%", "−4%"],
              ["YOLO Engine", "71.7%", "61.4%", "54.8%", "−17%"],
            ],
          },
        },
      ],
    },
    {
      heading: "5. Live Paper Trading Diagnostic (Mar 21–25 2026)",
      body: `Only ONE engine actively traded: HYPER_ACCURACY (136 trades). Free Will V2 generated 202 signals — all blocked by quant gate scoring 4–6/100 (data source failure, not signal failure).\n\nHYPER_ACCURACY Performance (114 closed):\n  Win Rate:      28.9% (33/114)\n  Total PnL:     −$11,271.56 USDT\n  Largest Win:   +$1,355.17 (APT/USDT)\n  Largest Loss:  −$2,202.61 (INJ/USDT, liquidation)\n  Avg Hold:      14.7 hours\n  Stop Loss:     75.4% of exits\n  Take Profit:   18.4% of exits\n  Liquidations:  4.4% of exits\n  Target R:R:    5.28:1\n\nTheoretical break-even at 5.28:1 R:R is 15.9% win rate. 28.9% should have been strongly profitable. Four execution bugs reversed this:\n\nBug 1 — SL = Entry Price (23.5% of trades). No downside protection → bleeds to liq.\nBug 2 — Liquidation above stop on leveraged shorts. INJ SHORT 20x: liq at 5%, stop at 7.5% → stop unreachable.\nBug 3 — Duplicate execution (45 groups fired 2–3×/sec).\nBug 4 — Price update loop stalled after PM2 restart (all open trades frozen at entry_price).\n\nQuant gate root cause: 3 dead endpoints (MEXC OI geo-blocked, Coinglass 404'd, MEXC L/S geo-blocked) + MEXC depth 3-element format bug. Fix: replaced all with OKX public stats API. Score: 4–6/100 → 41/100.`,
    },
    {
      heading: "6. ORIA Framework: Orthogonal Risk Integrated Alpha",
      body: `Four-layer risk and signal quality framework (oria_layer.py), wired at Gates 12–14.\n\nLayer 1 — Unified Signal Vector: 3+ engines agreeing in 10-min window → ELEVATED flag (+25% size bonus).\n\nLayer 2 — Edge/Cost Filter: 7d vs 30d per-symbol win rate. If 7d drops >15% below 30d → soft blacklist. Fee model: 0.06% taker × 50x lever = 3.0% per leg (6.0% round-trip) — kills YOLO/VWAP scalping edge.\n\nLayer 3 — Stress Score:\n  S(t) = 0.30·V(t) + 0.20·T(t) + 0.20·C(t) + 0.20·L(t) + 0.10·H(t)\n  V = volatility (ATR%/3.0 + BB_width/8.0)\n  T = trend uncertainty (1 − ADX/40)\n  C = crowding (funding + L/S ratio)\n  L = realized loss stress\n  H = system self-awareness (1 − H_value)\n  multiplier = clip(exp(−2.5 × S), 0.15, 1.00)\n\nLayer 4 — Staged Execution: spread cost model planned, full staged entry not yet implemented.`,
    },
    {
      heading: "7. TCN Neural Engine (Engine 9)",
      body: `Architecture: 6 dilated convolutional blocks · dilation [1,2,4,8,16,32] · 64 hidden channels\n12 Input Features: open, high, low, close, volume, ATR, RSI, MACD signal, MACD histogram, EMA-20Δ, EMA-50Δ, funding rate\nOutput: p̂ ∈ [0,1] directional probability (p̂ > 0.5 = bullish)\n\nQuantum contribution:\n  α₉(t) = (2p̂ − 1) × S₉\n\nThis maps TCN output to [−S₉, +S₉], contributing negative amplitude for bearish and positive for bullish. TCN votes (BULLISH/BEARISH/NEUTRAL) count at Gate 11 as a first-class engine vote. Finding: TCN/rule divergence reliably precedes below-average win-rate periods.`,
    },
    {
      heading: "8. NEXUS Autonomous Nervous System",
      body: `Five-layer service on port 8001:\n  1. Oracle    — market regime detection (TRENDING/RANGING/REVERSING/VOLATILE)\n  2. Morpheus  — hypothesis generation for strategy evolution\n  3. Healer    — health monitoring, per-key alert cooldowns (1h–12h)\n  4. Warden    — security and risk constraint enforcement\n  5. Core      — coordination, Telegram routing, config polling\n\nNEXUS feeds trend_regime into Gates 1 and 2. Key live event (June 2026): NEXUS correctly identified REVERSING regime while all 7 rule-based engines were generating 100% LONG signals. Gate 2 blocked or raised confidence requirements, preventing systematic losses.`,
    },
    {
      heading: "9. Memory Architecture",
      body: `memory_engine.py maintains trade_memories (MongoDB) with 200-record rolling cap. Each record stores: engine, symbol, direction, entry/stop/target prices, outcome, pnl_pct, confidence, leverage, feature_vector, env_regime, env_adx, env_btc_bias, quantum H/C/S, hour_of_day, day_of_week.\n\nPattern retrieval uses cosine similarity on feature_vector to find similar historical market conditions. Feeds M term in master equation.\n\nCritical bug (11 days silent): cursor stored as Python string, paper_trades.closed_at was BSON DateTime. Python datetime > str raised silent TypeError → 0 trades ingested with no log error. Fixed by type-checking in _get_processing_cursor(). 680 records backfilled in one poll cycle after fix.`,
    },
    {
      heading: "10. Core Empirical Findings",
      findings: [
        { id: "F1", title: "R:R is the dominant lever", detail: "+123% expectancy per 0.5R improvement at 62% WR. YOLO +3,469R from one change." },
        { id: "F2", title: "Redundant engines amplify risk", detail: "Day Trader ≈ VWAP (57.8% vs 58.1% WR). Double exposure, zero added edge." },
        { id: "F3", title: "Drawdown compounds non-linearly", detail: "YOLO: 8.1R max DD (500 trades) → 17.8R (10,000 trades). Backtests underestimate real risk." },
        { id: "F4", title: "Selectivity is durable", detail: "Elite: 67.9% WR stable across all 10,000 trades with lowest drawdown (7.0R)." },
        { id: "F5", title: "Sideways markets destroy momentum", detail: "YOLO −17% WR in sideways. Hurst Gate 10 designed specifically to block this." },
        { id: "F6", title: "Bear markets ≠ reduced edge", detail: "Elite: 74.7% bear WR > 72.9% bull WR. Clean trends + SHORT underutilization = edge." },
        { id: "F7", title: "Execution bugs > signal failures", detail: "−$11,271 paper loss at 28.9% WR / 5.28:1 R:R (should be strongly profitable)." },
        { id: "F8", title: "Data diversity = risk management", detail: "Quant gate: 4–6/100 → 41/100 after replacing dead MEXC/Coinglass endpoints with OKX." },
        { id: "F9", title: "Neural/rule divergence = regime signal", detail: "TCN-rule disagreement preceded below-average win-rate periods in live data." },
        { id: "F10", title: "Silent failures are most dangerous", detail: "11 days zero memory ingestion. No log error. TypeError on type mismatch returned empty quietly." },
        { id: "F11", title: "Security = financial risk control", detail: "Compromised webhook → injected signals. Auth boundaries are trading risk controls, not hygiene." },
      ],
    },
    {
      heading: "11. Five-Dimension Evaluation Framework",
      body: `Proposed framework for evaluating any autonomous crypto trading system:\n\nSQ — Signal Quality: Win rate × R:R composite in all 3 regimes. Min: positive in bull/bear/sideways.\nRA — Risk Architecture: Liq rate < 1%, max DD at 10k-trade convergence, correlated-loss stress response.\nGPC — Gate Pipeline Completeness: Must cover regime, bias, momentum quality, ensemble, quant scoring, fee-adjusted edge, stress.\nDDR — Data Dependency Resilience: 1 source fail → 80% function. 2 fail → 60%. 3 fail → halt cleanly.\nALF — Autonomous Learning Fidelity: Memory verifiable by count; Omega fires on H<1.0; Lθ proposals sandbox-validated; accepted changes persist; H improves measurably.\n\nAEON score: SQ ✓  RA ✓ (post-fix)  GPC ✓ (16 gates)  DDR ✓ (post-OKX)  ALF partial (memory restored June 2026; pending H > 0.30).`,
    },
    {
      heading: "12. Conclusion",
      body: `AEON demonstrates that a mathematically principled ensemble trading system with autonomous self-assessment and parameter modification is architecturally achievable on commodity infrastructure. The quantum-probabilistic identity framework provides a rigorous, interpretable, and computationally tractable language for measuring system health and governing execution decisions without human intervention.\n\nThe central empirical finding — reward-to-risk ratio is the dominant performance variable — has immediate implications: improvements to signal generation are significantly less valuable than improvements to exit timing and target setting.\n\nThe live paper trading diagnostic revealed that theoretical edge and realized edge diverge substantially in the presence of execution infrastructure bugs. Stop-loss geometry failures, liquidation ordering bugs, and data source failures each individually reversed profitable signal quality into net-negative outcomes.\n\nThe most durable finding: in leveraged derivative markets, survival is the prerequisite for profitability. Structural risk management must precede signal optimization. AEON was built in this order. Its 1,568-trade paper trading history — net negative due to infrastructure bugs now resolved — represents the empirical foundation from which genuine live edge can be measured and validated.`,
    },
  ],
};

// ── Paper Modal ───────────────────────────────────────────────────────────────

function PaperTable({ headers, rows }) {
  return (
    <div style={{ overflowX: "auto", marginBottom: 14 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{
                textAlign: "left", padding: "5px 8px", fontWeight: 700,
                color: "#93c5fd", borderBottom: "1px solid rgba(147,197,253,0.2)",
                letterSpacing: "0.06em", whiteSpace: "nowrap", fontFamily: "var(--font-mono)",
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} style={{ background: ri % 2 === 0 ? "rgba(255,255,255,0.02)" : "transparent" }}>
              {row.map((cell, ci) => (
                <td key={ci} style={{
                  padding: "5px 8px", color: "rgba(255,255,255,0.72)", fontSize: 10,
                  borderBottom: "1px solid rgba(255,255,255,0.04)", verticalAlign: "top",
                }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PaperSection({ section }) {
  if (section.subsections) {
    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#c4b5fd", marginBottom: 10, borderBottom: "1px solid rgba(196,181,253,0.15)", paddingBottom: 6 }}>
          {section.heading}
        </div>
        {section.subsections.map((sub, i) => (
          <div key={i} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#93c5fd", marginBottom: 8, letterSpacing: "0.05em" }}>{sub.label}</div>
            {sub.body && (
              <pre style={{ fontSize: 10, color: "rgba(255,255,255,0.65)", lineHeight: 1.7, whiteSpace: "pre-wrap", fontFamily: "var(--font-mono)", margin: 0 }}>
                {sub.body}
              </pre>
            )}
            {sub.table && <PaperTable headers={sub.table.headers} rows={sub.table.rows} />}
            {sub.gates && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {sub.gates.map((g, gi) => (
                  <div key={gi} style={{
                    fontSize: 10, color: "rgba(255,255,255,0.6)", fontFamily: "var(--font-mono)",
                    padding: "4px 10px", background: "rgba(92,242,255,0.03)",
                    borderLeft: "2px solid rgba(92,242,255,0.2)", borderRadius: "0 5px 5px 0",
                  }}>{g}</div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (section.findings) {
    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#c4b5fd", marginBottom: 10, borderBottom: "1px solid rgba(196,181,253,0.15)", paddingBottom: 6 }}>
          {section.heading}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {section.findings.map((f, i) => (
            <div key={i} style={{
              padding: "9px 12px", borderRadius: 8,
              background: "rgba(77,255,175,0.03)", border: "1px solid rgba(77,255,175,0.1)",
              display: "flex", gap: 10, alignItems: "flex-start",
            }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: "#4dffaf", fontFamily: "var(--font-mono)", flexShrink: 0, minWidth: 28 }}>{f.id}</span>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#e2e8f0", marginBottom: 2 }}>{f.title}</div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>{f.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#c4b5fd", marginBottom: 8, borderBottom: "1px solid rgba(196,181,253,0.15)", paddingBottom: 6 }}>
        {section.heading}
      </div>
      <pre style={{ fontSize: 10.5, color: "rgba(255,255,255,0.68)", lineHeight: 1.75, whiteSpace: "pre-wrap", fontFamily: "inherit", margin: 0 }}>
        {section.body}
      </pre>
    </div>
  );
}

function PaperModal({ onClose }) {
  const scrollRef = React.useRef(null);
  const [showTop, setShowTop] = useState(false);

  const handleScroll = useCallback(() => {
    setShowTop((scrollRef.current?.scrollTop || 0) > 300);
  }, []);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.addEventListener("scroll", handleScroll);
    return () => el?.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  React.useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.85)", backdropFilter: "blur(6px)",
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: "24px 16px",
    }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: "100%", maxWidth: 780, maxHeight: "calc(100vh - 48px)",
        background: "#0d1117", border: "1px solid rgba(196,181,253,0.2)",
        borderRadius: 14, display: "flex", flexDirection: "column", overflow: "hidden",
        boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
      }}>
        {/* Modal header */}
        <div style={{
          padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.07)",
          display: "flex", gap: 12, alignItems: "flex-start", flexShrink: 0,
          background: "rgba(196,181,253,0.04)",
        }}>
          <FileText style={{ width: 18, height: 18, color: "#c4b5fd", marginTop: 2, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0", lineHeight: 1.3, marginBottom: 3 }}>
              {AEON_PAPER.title}
            </div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.38)", lineHeight: 1.4 }}>
              {AEON_PAPER.subtitle}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 9, padding: "2px 7px", borderRadius: 4, background: "rgba(196,181,253,0.1)", border: "1px solid rgba(196,181,253,0.25)", color: "#c4b5fd", fontWeight: 700, letterSpacing: "0.1em" }}>
                AEON INTERNAL RESEARCH
              </span>
              <span style={{ fontSize: 9, color: "rgba(255,255,255,0.25)", fontFamily: "var(--font-mono)", alignSelf: "center" }}>
                {AEON_PAPER.date} · aeontrading.xyz
              </span>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 8, padding: "6px 8px", cursor: "pointer", color: "rgba(255,255,255,0.5)",
            display: "flex", alignItems: "center", flexShrink: 0,
          }}>
            <X style={{ width: 14, height: 14 }} />
          </button>
        </div>

        {/* Scrollable body */}
        <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {AEON_PAPER.sections.map((section, i) => (
            <PaperSection key={i} section={section} />
          ))}
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.18)", textAlign: "center", marginTop: 16, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.06)", fontFamily: "var(--font-mono)" }}>
            AEON System · Live paper trading at aeontrading.xyz · Research documented {AEON_PAPER.date}
          </div>
        </div>

        {/* Back-to-top */}
        {showTop && (
          <button
            onClick={() => scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" })}
            style={{
              position: "absolute", bottom: 80, right: 32,
              background: "rgba(196,181,253,0.15)", border: "1px solid rgba(196,181,253,0.3)",
              borderRadius: "50%", width: 36, height: 36,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: "#c4b5fd",
            }}
          >
            <ChevronUp style={{ width: 16, height: 16 }} />
          </button>
        )}
      </div>
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// ── Colour maps (CSS variables not reachable in JS objects) ──────────────────

const REGIME_STYLE = {
  MOMENTUM:       { text: "#4dffaf", bg: "rgba(77,255,175,0.08)",  border: "rgba(77,255,175,0.25)" },
  MEAN_REVERSION: { text: "#5cf2ff", bg: "rgba(92,242,255,0.08)",  border: "rgba(92,242,255,0.25)" },
  TRANSITIONAL:   { text: "#ffc24d", bg: "rgba(255,194,77,0.08)",  border: "rgba(255,194,77,0.25)" },
  RANDOM_WALK:    { text: "#ff5577", bg: "rgba(255,85,119,0.08)",  border: "rgba(255,85,119,0.25)" },
  UNKNOWN:        { text: "#94a3b8", bg: "rgba(148,163,184,0.06)", border: "rgba(148,163,184,0.2)" },
};

const VOTE_STYLE = {
  BULLISH: { text: "#4dffaf", bg: "rgba(77,255,175,0.1)",   border: "rgba(77,255,175,0.3)" },
  BEARISH: { text: "#ff5577", bg: "rgba(255,85,119,0.1)",   border: "rgba(255,85,119,0.3)" },
  NEUTRAL: { text: "#94a3b8", bg: "rgba(148,163,184,0.06)", border: "rgba(148,163,184,0.2)" },
};

// ── Small reusable atoms ──────────────────────────────────────────────────────

function Chip({ label, color, bg, border }) {
  return (
    <span className="rlab-chip" style={{ background: bg, border: `1px solid ${border}`, color }}>
      {label}
    </span>
  );
}

/**
 * In-panel heading. The hue used to colour the label text itself, which put
 * eight competing accent colours on one screen; it now lives in the icon and
 * a short rule, and the label reads in the standard foreground.
 */
function SectionLabel({ icon: Icon, label, sub, count, color = "#c4b5fd" }) {
  return (
    <div className="rlab-slabel">
      <span className="rlab-slabel-rule" style={{ background: color }} />
      <Icon className="rlab-slabel-icon" style={{ color }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="rlab-slabel-txt">{label}</div>
        {sub && <div className="rlab-slabel-sub">{sub}</div>}
      </div>
      {count !== undefined && <span className="rlab-slabel-count">{count}</span>}
    </div>
  );
}

function StatGrid({ items }) {
  return (
    <div className="rlab-statgrid" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
      {items.map(({ l, v, color }) => (
        <div key={l} className="rlab-stat-cell">
          <div className="rlab-stat-val" style={{ color }}>{v}</div>
          <div className="rlab-stat-lbl">{l}</div>
        </div>
      ))}
    </div>
  );
}

// ── Collapsible section — keeps secondary content out of the way until asked for ──

/**
 * Empty state that says WHICH kind of empty.
 *
 * Everything used to render the same grey box whether the panel had no data yet,
 * a correct zero, or a broken query — which is how a calibration endpoint that
 * matched 0 of 1,398 trades sat unnoticed. `kind` picks the wording and colour:
 *   "zero"    — the answer really is nothing, and that's fine
 *   "waiting" — nothing yet, but it's expected to fill
 *   "down"    — the connection failed; the panel is not authoritative
 */
function Empty({ kind = "waiting", children, detail }) {
  const meta = {
    zero:    { tone: "#4dffaf", lbl: "NOTHING TO SHOW" },
    waiting: { tone: "#647083", lbl: "NO DATA YET" },
    down:    { tone: "#f5b042", lbl: "FEED UNAVAILABLE" },
  }[kind] || { tone: "#647083", lbl: "NO DATA YET" };
  return (
    <div className="rlab-empty rlab-empty-typed" title={detail || undefined}>
      <span className="rlab-empty-tag" style={{ color: meta.tone, borderColor: `${meta.tone}55` }}>
        {meta.lbl}
      </span>
      <span className="rlab-empty-msg">{children}</span>
    </div>
  );
}

/** Text + chip filter over a long list. Returns the controlled query state. */
function FilterBar({ q, setQ, chips, active, setActive, placeholder, shown, total }) {
  return (
    <div className="rlab-filter">
      <input
        className="rlab-filter-input"
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder || "Filter by name, author or thesis…"}
        aria-label={placeholder || "Filter list"}
      />
      {(chips || []).length > 0 && (
        <div className="rlab-filter-chips">
          <button type="button" onClick={() => setActive(null)} style={_resChip(!active)}>all</button>
          {chips.map((c) => (
            <button key={c} type="button" onClick={() => setActive(active === c ? null : c)}
              style={_resChip(active === c)}>{c}</button>
          ))}
        </div>
      )}
      <span className="rlab-filter-count">
        {shown === total ? `${total}` : `${shown} of ${total}`}
      </span>
    </div>
  );
}

function Section({ title, sub, icon: Icon, color = "#93c5fd", defaultOpen = false, count, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rlab-card rlab-section">
      <button className="rlab-section-head" onClick={() => setOpen(o => !o)}>
        {Icon && <Icon style={{ width: 13, height: 13, color, flexShrink: 0 }} />}
        <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color, letterSpacing: "0.08em", textTransform: "uppercase" }}>{title}</div>
          {sub && <div style={{ fontSize: 9, color: "rgba(255,255,255,0.32)", marginTop: 1 }}>{sub}</div>}
        </div>
        {count !== undefined && <span style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", fontFamily: "var(--font-mono)" }}>{count}</span>}
        <ChevronUp style={{ width: 12, height: 12, color: "rgba(255,255,255,0.4)", flexShrink: 0, transform: open ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 160ms ease" }} />
      </button>
      {open && <div className="rlab-section-body">{children}</div>}
    </div>
  );
}


// ── Omega Cycle panel ─────────────────────────────────────────────────────────

function OmegaPanel({ omega, fixes }) {
  const summary = omega?.last_summary || {};
  const isLive  = omega?.status === "live";
  const oria    = omega?.oria_connected;
  const nextIn  = omega?.next_cycle_in_s;
  const nextStr = nextIn != null
    ? nextIn > 3600 ? `${Math.floor(nextIn / 3600)}h ${Math.floor((nextIn % 3600) / 60)}m`
    : nextIn > 60   ? `${Math.floor(nextIn / 60)}m`
    : `${nextIn}s`
    : "—";

  return (
    <div className="arena-equity-card">
      <SectionLabel icon={GitBranch} label="Omega Cycle Ω" sub="Self-improvement · 6h interval" color="#c4b5fd" />

      <StatGrid items={[
        { l: "Cycle",      v: omega?.cycle_count ?? "—",   color: "#e2e8f0" },
        { l: "ORIA Wired", v: oria ? "YES" : "NO",         color: oria ? "#4dffaf" : "#ff5577" },
        { l: "Gov Writes", v: omega?.governance_writes ? "YES" : "NO", color: "#4dffaf" },
        { l: "Next In",    v: nextStr,                      color: "#ffc24d" },
      ]} />

      {summary.triggered && (
        <div style={{ background: "rgba(245,158,11,0.05)", border: "1px solid rgba(245,158,11,0.18)", borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: "#fcd34d", fontWeight: 700, marginBottom: 6 }}>
            LAST TRIGGER — Cycle #{summary.cycle}
          </div>
          {(summary.reasons || []).slice(0, 3).map((r, i) => (
            <div key={i} style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginBottom: 3, display: "flex", gap: 6 }}>
              <span style={{ color: "#fcd34d", flexShrink: 0 }}>·</span>
              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r}</span>
            </div>
          ))}
          {(summary.applied || []).length > 0 && (
            <div style={{ marginTop: 8, borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 6 }}>
              <div className="rlab-section-lbl" style={{ marginBottom: 4 }}>Auto-applied</div>
              {summary.applied.map((a, i) => (
                <div key={i} style={{ fontSize: 10, color: "#4dffaf" }}>✓ {a.engine} → {a.param}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {Array.isArray(fixes) && fixes.length === 0 && (
        <div>
          <div className="rlab-section-lbl">Recent fixes</div>
          <Empty kind="zero" detail={`OMEGA runs every ${omega?.interval_h ?? 6}h; it is on cycle ${omega?.cycle_count ?? 0}.`}>
            OMEGA has found nothing to self-heal. An empty log is the healthy state.
          </Empty>
        </div>
      )}
      {Array.isArray(fixes) && fixes.length > 0 && (
        <div>
          <div className="rlab-section-lbl">Recent fixes</div>
          {fixes.slice(0, 4).map((f, i) => {
            const isLth = f.fix_type === "Lθ";
            return (
              <div key={i} className="rlab-fix-row">
                <Chip
                  label={f.fix_type || "Lθ"}
                  color={isLth ? "#4dffaf" : "#fcd34d"}
                  bg={isLth ? "rgba(77,255,175,0.08)" : "rgba(255,194,77,0.08)"}
                  border={isLth ? "rgba(77,255,175,0.25)" : "rgba(255,194,77,0.25)"}
                />
                <span className="rlab-fix-desc">
                  {f.engine} — {f.param || "structural"}{f.direction ? ` → ${f.direction}` : ""}
                </span>
                <span style={{ fontSize: 9, color: f.auto_applied ? "#4dffaf" : "#f87171", flexShrink: 0 }}>
                  {f.auto_applied ? "applied" : "pending"}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {!isLive && (
        <div style={{ textAlign: "center", color: "rgba(255,255,255,0.22)", fontSize: 11, padding: "12px 0" }}>
          Omega Cycle initialises 5 min after backend boot.
        </div>
      )}
    </div>
  );
}

// ── NEXUS panel ───────────────────────────────────────────────────────────────

function NexusPanel({ nexus }) {
  const cfg     = nexus?.config || {};
  const isLive  = nexus?.live || false;
  const isCrisis = cfg.crisis || cfg.pause;
  const mod     = cfg.position_modifier ?? 1.0;
  const modColor = mod >= 0.9 ? "#4dffaf" : mod >= 0.6 ? "#ffc24d" : "#ff5577";

  return (
    <div className="arena-equity-card">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <Shield style={{ width: 13, height: 13, color: isLive ? "#4dffaf" : "#f87171", flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: isLive ? "#4dffaf" : "#f87171", letterSpacing: "0.1em", textTransform: "uppercase" }}>
            NEXUS · Autonomous Nervous System
          </div>
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", marginTop: 1 }}>
            Oracle → Morpheus → Healer → Warden · 60s loop
          </div>
        </div>
        <span style={{
          fontSize: 9, padding: "2px 9px", borderRadius: 20, flexShrink: 0,
          border: `1px solid ${isLive ? "rgba(77,255,175,0.3)" : "rgba(248,113,113,0.3)"}`,
          color: isLive ? "#4dffaf" : "#f87171",
          background: isLive ? "rgba(77,255,175,0.07)" : "rgba(248,113,113,0.07)",
          fontFamily: "var(--font-mono)", letterSpacing: "0.12em",
        }}>
          {isLive ? "LIVE" : "OFFLINE"}
        </span>
      </div>

      <StatGrid items={[
        { l: "Regime",  v: cfg.regime || "—",        color: "#e2e8f0" },
        { l: "Trend",   v: cfg.trend_regime || "—",  color: "#94a3b8" },
        { l: "Size ×",  v: mod.toFixed(2),            color: modColor  },
        { l: "Status",  v: isCrisis ? "PAUSED" : "ACTIVE", color: isCrisis ? "#f87171" : "#4dffaf" },
      ]} />

      {cfg.reason && (
        <div style={{
          fontSize: 10, color: "rgba(255,255,255,0.42)", fontStyle: "italic",
          marginBottom: 12, padding: "7px 11px",
          background: "rgba(255,255,255,0.02)", borderRadius: 7,
          borderLeft: "2px solid rgba(196,181,253,0.3)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {cfg.reason}
        </div>
      )}

      {nexus?.recent_heals?.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div className="rlab-section-lbl" style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <Heart style={{ width: 9, height: 9, color: "#f87171" }} /> Healer — recent actions
          </div>
          {nexus.recent_heals.slice(0, 3).map((h, i) => (
            <div key={i} style={{ display: "flex", gap: 8, fontSize: 10, color: "rgba(255,255,255,0.45)", marginBottom: 3 }}>
              <span style={{ color: "#f87171", flexShrink: 0 }}>·</span>
              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {h.heal_type} — {h.target || h.engine || "system"}
              </span>
              {h.result && (
                <span style={{ flexShrink: 0, color: "rgba(255,255,255,0.2)", fontSize: 9 }}>
                  {String(h.result).slice(0, 28)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {nexus?.recent_adaptations?.length > 0 && (
        <div>
          <div className="rlab-section-lbl" style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <Eye style={{ width: 9, height: 9, color: "#c4b5fd" }} /> Morpheus — regime changes
          </div>
          {nexus.recent_adaptations.slice(0, 2).map((a, i) => (
            <div key={i} style={{ background: "rgba(196,181,253,0.04)", border: "1px solid rgba(196,181,253,0.14)", borderRadius: 7, padding: "7px 11px", marginBottom: 4 }}>
              <div style={{ fontSize: 10, color: "#c4b5fd", fontWeight: 600 }}>{a.regime} + {a.trend_regime}</div>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.38)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {a.reason || a.summary || "Regime adaptation applied"}
              </div>
            </div>
          ))}
        </div>
      )}

      {!nexus && (
        <div style={{ textAlign: "center", color: "rgba(255,255,255,0.22)", fontSize: 11, padding: "16px 0" }}>
          Awaiting NEXUS heartbeat…
        </div>
      )}
    </div>
  );
}

// ── Papers panel ──────────────────────────────────────────────────────────────

const paperSourceUrl = (paper) => paper?.arxiv_id
  ? `https://arxiv.org/abs/${paper.arxiv_id}`
  : (paper?.link || paper?.arxiv_url || paper?.url || "");

const paperActionStyle = (accent = false) => ({
  display: "inline-flex", alignItems: "center", gap: 5,
  borderRadius: 6, padding: "5px 8px", cursor: "pointer",
  fontSize: 9, fontWeight: 750, letterSpacing: "0.04em",
  border: `1px solid ${accent ? "rgba(196,181,253,0.35)" : "rgba(147,197,253,0.22)"}`,
  background: accent ? "rgba(196,181,253,0.1)" : "rgba(147,197,253,0.06)",
  color: accent ? "#c4b5fd" : "#93c5fd", textDecoration: "none",
});

function BriefList({ items, empty = "Not established from the available source material." }) {
  const rows = Array.isArray(items) ? items.filter(Boolean) : [];
  if (!rows.length) {
    return <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.3)", fontStyle: "italic" }}>{empty}</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {rows.map((item, i) => (
        <div key={i} style={{ display: "flex", gap: 8, fontSize: 10.5, lineHeight: 1.55, color: "rgba(255,255,255,0.68)" }}>
          <span style={{ color: "#c4b5fd", flexShrink: 0 }}>•</span>
          <span>{typeof item === "string" ? item : JSON.stringify(item)}</span>
        </div>
      ))}
    </div>
  );
}

function BriefSection({ title, color = "#93c5fd", children }) {
  return (
    <section style={{
      border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10,
      background: "rgba(255,255,255,0.025)", padding: "12px 13px",
    }}>
      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color, marginBottom: 8 }}>
        {title}
      </div>
      {children}
    </section>
  );
}

export function PaperBriefModal({ paper, onClose }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const url = paperSourceUrl(paper);

  useEffect(() => {
    let alive = true;
    setPayload(null); setError(""); setLoading(true);
    axios.post(`${API}/researcher/papers/brief`, null, {
      // A cold brief is a ~60s local-model synthesis that also queues for the single
      // GPU slot; 90s cut it off mid-generation. Warmed briefs return instantly.
      params: { arxiv_id: paper?.arxiv_id }, timeout: 240000,
    }).then((res) => {
      if (alive) setPayload(res.data);
    }).catch((err) => {
      if (alive) setError(err?.response?.data?.detail || err?.message || "Unable to build this brief.");
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [paper?.arxiv_id]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  if (!paper) return null;
  const brief = payload?.brief || {};
  const aeon = brief.aeon_application || {};
  const recommendation = brief.recommendation || {};
  const blueprint = Array.isArray(brief.implementation_blueprint) ? brief.implementation_blueprint : [];
  const statusColor = payload?.generation_status === "generated" ? "#4dffaf" : "#fbbf24";

  return createPortal(
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 10020, background: "rgba(0,0,0,0.84)",
      backdropFilter: "blur(7px)", display: "flex", alignItems: "center",
      justifyContent: "center", padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "100%", maxWidth: 860, maxHeight: "90vh", background: "#0b1020",
        border: "1px solid rgba(196,181,253,0.26)", borderRadius: 16,
        boxShadow: "0 28px 90px rgba(0,0,0,0.72)", display: "flex",
        flexDirection: "column", overflow: "hidden",
      }}>
        <div style={{
          padding: "15px 18px", borderBottom: "1px solid rgba(255,255,255,0.07)",
          display: "flex", alignItems: "flex-start", gap: 10,
          background: "linear-gradient(135deg, rgba(196,181,253,0.09), rgba(92,242,255,0.025))",
        }}>
          <Sparkles style={{ width: 18, height: 18, color: "#c4b5fd", marginTop: 2, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.14em", color: "#c4b5fd", textTransform: "uppercase", marginBottom: 4 }}>
              AEON Research Intelligence · Integration Brief
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.35, color: "#edf2ff" }}>
              {payload?.title || paper.title || "Research paper"}
            </div>
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 7, alignItems: "center" }}>
              {paper.arxiv_id && <span style={{ fontSize: 9, color: "rgba(147,197,253,0.62)", fontFamily: "var(--font-mono)" }}>arXiv {paper.arxiv_id}</span>}
              {payload && <span style={{ fontSize: 8.5, color: statusColor, border: `1px solid ${statusColor}44`, borderRadius: 5, padding: "2px 6px" }}>
                {payload.cached ? "CACHED BRIEF" : payload.generation_status === "generated" ? "LOCAL-MODEL BRIEF" : "SOURCE-LIMITED BRIEF"}
              </span>}
              {payload?.generated_at && <span style={{ fontSize: 8.5, color: "rgba(255,255,255,0.25)" }}>{new Date(payload.generated_at).toLocaleString()}</span>}
            </div>
          </div>
          {url && (
            <a href={url} target="_blank" rel="noopener noreferrer" style={paperActionStyle(false)}>
              Original <ExternalLink style={{ width: 11, height: 11 }} />
            </a>
          )}
          <button onClick={onClose} aria-label="Close research brief" style={{
            display: "flex", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.09)",
            borderRadius: 7, padding: 6, color: "rgba(255,255,255,0.55)", cursor: "pointer",
          }}><X style={{ width: 15, height: 15 }} /></button>
        </div>

        <div style={{ overflowY: "auto", padding: 16 }}>
          {loading && (
            <div style={{ minHeight: 260, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10 }}>
              <RefreshCw style={{ width: 22, height: 22, color: "#c4b5fd", animation: "spin 1s linear infinite" }} />
              <div style={{ fontSize: 12, color: "#e2e8f0", fontWeight: 650 }}>Building the AEON implementation brief…</div>
              <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.3)", textAlign: "center", maxWidth: 430, lineHeight: 1.5 }}>
                Extracting the thesis, evidence, limitations, integration points, guardrails, and validation plan. The result is cached for future opens.
              </div>
            </div>
          )}

          {!loading && error && (
            <div style={{ minHeight: 220, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10 }}>
              <AlertTriangle style={{ width: 24, height: 24, color: "#fbbf24" }} />
              <div style={{ fontSize: 11, color: "#fbbf24" }}>{error}</div>
              {url && <a href={url} target="_blank" rel="noopener noreferrer" style={paperActionStyle(false)}>Open original paper <ExternalLink style={{ width: 11, height: 11 }} /></a>}
            </div>
          )}

          {!loading && !error && payload && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <BriefSection title="Executive synthesis" color="#c4b5fd">
                <div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.76)", lineHeight: 1.72, whiteSpace: "pre-wrap" }}>
                  {brief.executive_summary || "No executive synthesis returned."}
                </div>
              </BriefSection>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 10 }}>
                <BriefSection title="Research question">
                  <div style={{ fontSize: 10.8, lineHeight: 1.62, color: "rgba(255,255,255,0.68)" }}>{brief.research_question || "Not stated."}</div>
                </BriefSection>
                <BriefSection title="Methodology">
                  <div style={{ fontSize: 10.8, lineHeight: 1.62, color: "rgba(255,255,255,0.68)" }}>{brief.methodology || "Not stated."}</div>
                </BriefSection>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10 }}>
                <BriefSection title="Key findings" color="#4dffaf"><BriefList items={brief.key_findings} /></BriefSection>
                <BriefSection title="Assumptions & limitations" color="#fbbf24"><BriefList items={brief.assumptions_and_limitations} /></BriefSection>
              </div>

              <BriefSection title="How this maps into AEON" color="#5cf2ff">
                <div style={{ fontSize: 11, lineHeight: 1.65, color: "rgba(255,255,255,0.76)", marginBottom: 10 }}>{aeon.fit_summary || "No AEON fit assessment returned."}</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 12 }}>
                  <div><div style={{ fontSize: 8.5, color: "#5cf2ff", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".1em" }}>Target components</div><BriefList items={aeon.target_components} /></div>
                  <div><div style={{ fontSize: 8.5, color: "#5cf2ff", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".1em" }}>Proposed changes</div><BriefList items={aeon.proposed_changes} /></div>
                </div>
              </BriefSection>

              <BriefSection title="Implementation blueprint" color="#c4b5fd">
                {blueprint.length ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {blueprint.map((phase, i) => (
                      <div key={i} style={{ borderLeft: "2px solid rgba(196,181,253,0.45)", padding: "7px 10px", background: "rgba(196,181,253,0.035)", borderRadius: "0 8px 8px 0" }}>
                        <div style={{ fontSize: 10.5, fontWeight: 750, color: "#e2e8f0", marginBottom: 5 }}>
                          {phase.phase ? `${phase.phase} · ` : ""}{phase.title || `Phase ${i + 1}`}
                        </div>
                        <BriefList items={phase.actions} />
                        {phase.deliverable && <div style={{ fontSize: 9.5, color: "rgba(92,242,255,0.65)", marginTop: 7 }}><b>Deliverable:</b> {phase.deliverable}</div>}
                        {phase.acceptance_gate && <div style={{ fontSize: 9.5, color: "rgba(77,255,175,0.65)", marginTop: 3 }}><b>Acceptance gate:</b> {phase.acceptance_gate}</div>}
                      </div>
                    ))}
                  </div>
                ) : <BriefList items={[]} />}
              </BriefSection>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 10 }}>
                <BriefSection title="Data requirements"><BriefList items={brief.data_requirements} /></BriefSection>
                <BriefSection title="Validation plan" color="#4dffaf"><BriefList items={brief.validation_plan} /></BriefSection>
                <BriefSection title="Risks & guardrails" color="#fb7185"><BriefList items={brief.risks_and_guardrails} /></BriefSection>
              </div>

              <div style={{
                border: "1px solid rgba(77,255,175,0.18)", borderRadius: 10, padding: "12px 13px",
                background: "linear-gradient(90deg, rgba(77,255,175,0.05), rgba(196,181,253,0.04))",
                display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap",
              }}>
                <div style={{ minWidth: 86 }}>
                  <div style={{ fontSize: 8.5, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: ".1em" }}>Decision</div>
                  <div style={{ fontSize: 13, color: "#4dffaf", fontWeight: 800, marginTop: 3 }}>{recommendation.decision || "REVIEW"}</div>
                </div>
                <div style={{ minWidth: 86 }}>
                  <div style={{ fontSize: 8.5, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: ".1em" }}>Readiness</div>
                  <div style={{ fontSize: 13, color: "#c4b5fd", fontWeight: 800, marginTop: 3 }}>{recommendation.readiness_score ?? "—"}/100</div>
                </div>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontSize: 8.5, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: ".1em" }}>Recommendation</div>
                  <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.67)", lineHeight: 1.55, marginTop: 3 }}>{recommendation.rationale || "Manual research review required."}</div>
                </div>
              </div>

              <div style={{ fontSize: 8.5, color: "rgba(255,255,255,0.22)", lineHeight: 1.5, textAlign: "center", padding: "2px 8px" }}>
                Evidence basis: {brief.evidence_basis || payload.evidence_basis || "available metadata"}. This is an implementation proposal, not an automatic production change.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function PaperCard({ p, onOpen, onBrief }) {
  const url = paperSourceUrl(p);
  const isInternal = p.internal;

  const inner = (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        {isInternal && (
          <div style={{ marginBottom: 5 }}>
            <span style={{
              fontSize: 8.5, fontWeight: 800, letterSpacing: "0.12em", padding: "2px 7px",
              borderRadius: 4, textTransform: "uppercase",
              background: "rgba(196,181,253,0.12)", border: "1px solid rgba(196,181,253,0.3)", color: "#c4b5fd",
            }}>AEON Internal Research</span>
          </div>
        )}
        <div style={{
          fontSize: 11, fontWeight: 600, lineHeight: 1.4, marginBottom: 4,
          color: isInternal ? "#c4b5fd" : (url ? "#93c5fd" : "#e2e8f0"),
          overflow: "hidden", display: "-webkit-box",
          WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
        }}>
          {p.title || "Untitled"}
          {!isInternal && url && <span style={{ fontSize: 9, marginLeft: 5, opacity: 0.5 }}>↗</span>}
          {isInternal && <span style={{ fontSize: 9, marginLeft: 5, opacity: 0.5 }}>→ read</span>}
        </div>
        {p.abstract_summary && (
          <div style={{
            fontSize: 9.5, color: "rgba(255,255,255,0.38)", lineHeight: 1.5,
            overflow: "hidden", display: "-webkit-box",
            WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          }}>
            {p.abstract_summary}
          </div>
        )}
        {p.date && isInternal && (
          <div style={{ fontSize: 8.5, color: "rgba(196,181,253,0.35)", marginTop: 5, fontFamily: "var(--font-mono)" }}>
            {p.date} · aeontrading.xyz
          </div>
        )}
        {p.arxiv_id && (
          <div style={{ fontSize: 8.5, color: "rgba(147,197,253,0.4)", marginTop: 5, fontFamily: "var(--font-mono)" }}>
            arxiv.org/abs/{p.arxiv_id}
          </div>
        )}
      </div>
      {p.relevance_score != null && (
        <div style={{ textAlign: "center", flexShrink: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: p.relevance_score >= 0.7 ? "#4dffaf" : "#ffc24d", fontFamily: "var(--font-mono)" }}>
            {Math.round(p.relevance_score * 100)}
          </div>
          <div style={{ fontSize: 8, color: "rgba(255,255,255,0.25)" }}>score</div>
        </div>
      )}
    </div>
  );

  const openPrimary = () => {
    if (isInternal) onOpen?.();
    else if (url) window.open(url, "_blank", "noopener,noreferrer");
  };
  return (
    <div
      className={`rlab-paper-card${(url || isInternal) ? " is-link" : ""}`}
      role="link" tabIndex={0}
      onClick={openPrimary}
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); openPrimary(); } }}
      style={isInternal ? {
        cursor: "pointer", background: "rgba(196,181,253,0.04)",
        border: "1px solid rgba(196,181,253,0.2)",
      } : undefined}
    >
      {inner}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 9, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.055)" }}>
        <button onClick={(e) => { e.stopPropagation(); openPrimary(); }} style={paperActionStyle(false)}>
          {isInternal ? "Read full paper" : "Open paper"} <ExternalLink style={{ width: 10, height: 10 }} />
        </button>
        {!isInternal && p.arxiv_id && (
          <button onClick={(e) => { e.stopPropagation(); onBrief?.(p); }} style={paperActionStyle(true)}>
            <Sparkles style={{ width: 10, height: 10 }} /> AEON integration brief
          </button>
        )}
      </div>
    </div>
  );
}

const _resChip = (active) => ({
  fontSize: 9.5, padding: "4px 9px", borderRadius: 999, cursor: "pointer",
  border: `1px solid ${active ? "rgba(196,181,253,0.5)" : "rgba(255,255,255,0.1)"}`,
  background: active ? "rgba(196,181,253,0.15)" : "rgba(255,255,255,0.03)",
  color: active ? "#c4b5fd" : "rgba(255,255,255,0.5)", fontWeight: 700,
});
const _resScoreColor = (s) =>
  s == null ? "rgba(255,255,255,0.3)" : s >= 80 ? "#4dffaf" : s >= 60 ? "#fbbf24" : "rgba(255,255,255,0.5)";

function ModelLanePanel({ configs }) {
  if (!configs || configs.length === 0) {
    return (
      <div className="arena-equity-card">
        <SectionLabel icon={Cpu} label="Model Lane" sub="self-built ML models · evolutionary config search" color="#5cf2ff" count={0} />
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", fontStyle: "italic", padding: "8px 2px" }}>
          First training cycle pending — the lab seeds, tunes and prunes its own models daily.
        </div>
      </div>
    );
  }
  return (
    <div className="arena-equity-card">
      <SectionLabel icon={Cpu} label="Model Lane" sub="self-built ML models · lab picks algo, features, tuning" color="#5cf2ff" count={configs.length} />
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {configs.map((c, i) => (
          <div key={c.id || i} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 10, padding: "6px 9px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 7 }}>
            <Chip
              label={c.live ? "LIVE" : "FLAT"}
              color={c.live ? "#4dffaf" : "rgba(255,255,255,0.35)"}
              bg={c.live ? "rgba(77,255,175,0.08)" : "rgba(255,255,255,0.04)"}
              border={c.live ? "rgba(77,255,175,0.25)" : "rgba(255,255,255,0.1)"}
            />
            <span style={{ flex: 1, minWidth: 0, color: "#e2e8f0", fontFamily: "var(--font-mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {(c.algo || "?").toUpperCase()} · {(c.sym || "").split("/")[0]} · h{c.horizon} · {(c.feats || []).length}f · g{c.gen}
            </span>
            <span style={{ flexShrink: 0, color: "rgba(255,255,255,0.4)" }}>
              acc {c.oos_acc != null ? Math.round(c.oos_acc * 100) + "%" : "—"}
            </span>
            <span style={{ flexShrink: 0, fontWeight: 700, fontFamily: "var(--font-mono)", color: (c.oos_edge_pct ?? 0) > 0 ? "#4dffaf" : "#fb7185" }}>
              {c.oos_edge_pct != null ? (c.oos_edge_pct > 0 ? "+" : "") + c.oos_edge_pct.toFixed(3) + "%/bar" : "unscored"}
            </span>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.28)", marginTop: 8, lineHeight: 1.5 }}>
        Retrained daily on fresh candles. FLAT = no hold-out edge, so the model refuses to trade until it re-earns it.
        Winners breed mutants (features toggled, hyperparams shifted, horizon changed); losers are bred out.
      </div>
    </div>
  );
}

const _intelTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toISOString().slice(11, 16) + " UTC"; } catch { return ""; }
};

function IntelStat({ label, value, delta, color = "#5cf2ff", fmt = fmtCompact }) {
  if (value == null) return null;
  return (
    <div style={{ flex: 1, minWidth: 108, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "8px 10px" }}>
      <div style={{ fontSize: 8.5, letterSpacing: ".12em", color: "rgba(255,255,255,0.4)", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 800, color, marginTop: 3, fontFamily: "var(--font-mono)" }}>{fmt(value)}</div>
      {delta != null && delta !== 0 && (
        <div style={{ fontSize: 9, color: delta >= 0 ? "#4dffaf" : "#fb7185", marginTop: 1 }}>{delta >= 0 ? "▲" : "▼"} {fmt(Math.abs(delta))}</div>
      )}
    </div>
  );
}

function OkxIntelPanel({ sources }) {
  if (!sources || Object.keys(sources).length === 0) {
    return <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", fontStyle: "italic", padding: "10px 2px" }}>No OKX intel snapshots yet — hourly ingest populates this.</div>;
  }
  const flow = ["btc", "eth", "sol"].map(s => ({ sym: s.toUpperCase(), src: sources[`okx_perp_flow_${s}`] })).filter(x => x.src);
  const sm = sources.okx_tracker_smart_money;
  const sentiment = sources.okx_sentiment_majors?.summary || {};
  const boards = ["ethereum", "solana"].map(c => ({ chain: c, src: sources[`okx_leaderboard_${c}`] })).filter(x => x.src);
  const newest = Object.values(sources).map(s => s.fetched_at).sort().pop();
  return (
    <div className="arena-equity-card">
      <SectionLabel icon={Activity} label="OKX Intel" sub={`smart money · sentiment · perp flow · as of ${_intelTime(newest)}`} color="#fbbf24" count={Object.keys(sources).length} />
      {flow.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 9, letterSpacing: ".1em", color: "rgba(255,255,255,0.45)", textTransform: "uppercase", marginBottom: 6 }}>Perp flow</div>
          {flow.map(({ sym, src }) => {
            const s = src.summary || {}, d = src.deltas || {};
            return (
              <div key={sym} style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "stretch" }}>
                <div style={{ width: 40, display: "flex", alignItems: "center", fontSize: 11, fontWeight: 800, color: "#e2e8f0", fontFamily: "var(--font-mono)" }}>{sym}</div>
                <IntelStat label="OI (USD)" value={s.oi_usd} delta={d.oi_usd} color="#5cf2ff" fmt={fmtUsdCompact} />
                <IntelStat label="Funding" value={s.funding_rate} delta={d.funding_rate} color={s.funding_rate >= 0 ? "#4dffaf" : "#fb7185"} fmt={(v) => `${(v * 100).toFixed(4)}%`} />
                <IntelStat label="Long/Short" value={s.long_short_ratio} delta={d.long_short_ratio} color="#c4b5fd" fmt={(v) => Number(v).toFixed(2)} />
              </div>
            );
          })}
        </div>
      )}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        {sm?.summary && <>
          <IntelStat label="Smart-money trades" value={sm.summary.trade_count} delta={sm.deltas?.trade_count} color="#fbbf24" />
          <IntelStat label="SM realized PnL" value={sm.summary.realized_pnl_usd} delta={sm.deltas?.realized_pnl_usd} color={(sm.summary.realized_pnl_usd || 0) >= 0 ? "#4dffaf" : "#fb7185"} fmt={fmtUsdCompact} />
        </>}
        {boards.map(({ chain, src }) => (
          <IntelStat key={chain} label={`${chain} top-50 PnL`} value={src.summary?.total_realized_pnl_usd} delta={src.deltas?.total_realized_pnl_usd} color="#93c5fd" fmt={fmtUsdCompact} />
        ))}
      </div>
      {Object.keys(sentiment).length > 0 && (
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {Object.entries(sentiment).filter(([, v]) => v && typeof v === "object").map(([coin, v]) => (
            <span key={coin} style={{
              fontSize: 9, padding: "3px 8px", borderRadius: 6, fontFamily: "var(--font-mono)",
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
              color: v.label === "bullish" ? "#4dffaf" : v.label === "bearish" ? "#fb7185" : "rgba(255,255,255,0.55)",
            }}>{coin} {v.label} · {Math.round((v.bullish_ratio || 0) * 100)}% bull</span>
          ))}
        </div>
      )}
    </div>
  );
}

function OnchainPanel({ sources }) {
  if (!sources || Object.keys(sources).length === 0) {
    return <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", fontStyle: "italic", padding: "10px 2px" }}>No on-chain snapshots yet — hourly ingest populates this.</div>;
  }
  const chain = sources.btc_chain, mem = sources.btc_mempool;
  const newest = Object.values(sources).map(s => s.fetched_at).sort().pop();
  return (
    <div className="arena-equity-card">
      <SectionLabel icon={GitBranch} label="On-chain" sub={`BTC network · mempool · as of ${_intelTime(newest)}`} color="#4dffaf" count={Object.keys(sources).length} />
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {chain?.data && <>
          <IntelStat label="Hash rate (EH/s)" value={chain.data.hash_rate_eh} delta={chain.deltas?.hash_rate_eh} color="#fbbf24" />
          <IntelStat label="Tx 24h" value={chain.data.n_tx_24h} delta={chain.deltas?.n_tx_24h} color="#5cf2ff" />
          <IntelStat label="BTC sent 24h" value={chain.data.estimated_btc_sent_24h} delta={chain.deltas?.estimated_btc_sent_24h} color="#93c5fd" />
        </>}
        {mem?.data && <>
          <IntelStat label="Fastest fee (sat/vB)" value={mem.data.fees_sat_vb?.fastest} delta={mem.deltas?.["fees_sat_vb.fastest"]} color="#4dffaf" fmt={(v) => Number(v).toFixed(1)} />
          <IntelStat label="Mempool tx" value={mem.data.mempool?.tx_count} delta={mem.deltas?.["mempool.tx_count"]} color="#c4b5fd" />
        </>}
      </div>
    </div>
  );
}

function WeexIntelPanel({ sources }) {
  if (!sources || Object.keys(sources).length === 0) {
    return <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", fontStyle: "italic", padding: "10px 2px" }}>No WEEX intel snapshots yet — hourly ingest populates this.</div>;
  }
  const acct = sources.weex_account;
  const fb = sources.weex_funding_basis;
  const depth = sources.weex_depth;
  const listings = sources.weex_listings;
  const funded = !!acct?.summary?.funded;
  const newListings = (listings?.rows || []).filter(r => r.change === "listed");
  const newest = Object.values(sources).map(s => s.fetched_at).sort().pop();
  const bps = (v) => v == null ? "—" : `${Number(v).toFixed(2)} bps`;
  const pct = (v) => v == null ? "—" : `${(v * 100).toFixed(4)}%`;
  return (
    <div className="arena-equity-card">
      <SectionLabel icon={Zap} label="WEEX Intel" sub={`funding gap vs OKX · depth · listings · account · as of ${_intelTime(newest)}`} color="#f97316" count={Object.keys(sources).length} />

      {acct && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10, alignItems: "stretch" }}>
          <div style={{ display: "flex", alignItems: "center", padding: "0 10px", borderRadius: 8, fontSize: 10, fontWeight: 800, letterSpacing: ".08em", fontFamily: "var(--font-mono)",
            background: funded ? "rgba(77,255,175,0.08)" : "rgba(255,255,255,0.03)",
            border: `1px solid ${funded ? "rgba(77,255,175,0.35)" : "rgba(255,255,255,0.08)"}`,
            color: funded ? "#4dffaf" : "rgba(255,255,255,0.4)" }}>
            {funded ? "● FUNDED" : "○ UNFUNDED"}
          </div>
          <IntelStat label="Swap USDT" value={acct.summary?.swap_usdt} delta={acct.deltas?.swap_usdt} color="#4dffaf" fmt={fmtUsdCompact} />
          <IntelStat label="Open positions" value={acct.summary?.open_positions} delta={acct.deltas?.open_positions} color="#5cf2ff" fmt={(v) => String(v)} />
          {funded && <IntelStat label="uPnL" value={acct.summary?.total_upnl} delta={acct.deltas?.total_upnl} color={(acct.summary?.total_upnl || 0) >= 0 ? "#4dffaf" : "#fb7185"} fmt={fmtUsdCompact} />}
        </div>
      )}

      {(fb?.rows || []).length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 9, letterSpacing: ".1em", color: "rgba(255,255,255,0.45)", textTransform: "uppercase", marginBottom: 6 }}>Funding gap vs OKX (WEEX retail lean)</div>
          {fb.rows.map((r) => {
            const dRow = (depth?.rows || []).find(x => x.symbol === r.symbol) || {};
            return (
              <div key={r.symbol} style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "stretch" }}>
                <div style={{ width: 40, display: "flex", alignItems: "center", fontSize: 11, fontWeight: 800, color: "#e2e8f0", fontFamily: "var(--font-mono)" }}>{r.symbol}</div>
                <IntelStat label="WEEX funding" value={r.weex_funding} color={(r.weex_funding || 0) >= 0 ? "#4dffaf" : "#fb7185"} fmt={pct} />
                <IntelStat label="Funding gap" value={r.funding_gap} color={(r.funding_gap || 0) >= 0 ? "#fbbf24" : "#c4b5fd"} fmt={pct} />
                <IntelStat label="Basis" value={r.basis_bps} color="#5cf2ff" fmt={bps} />
                <IntelStat label="Spread" value={dRow.spread_bps} color="#93c5fd" fmt={bps} />
              </div>
            );
          })}
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.28)", lineHeight: 1.5 }}>
            Positive gap = WEEX crowd longer than OKX on that coin. Spread/slippage measured from the live book — fill calibration for real execution.
          </div>
        </div>
      )}

      {listings && (
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 9, color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: ".1em" }}>Listing radar</span>
          <span style={{ fontSize: 9, padding: "3px 8px", borderRadius: 6, fontFamily: "var(--font-mono)", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.55)" }}>
            {fmtCompact(listings.summary?.total)} markets · {fmtCompact(listings.summary?.spot)} spot · {fmtCompact(listings.summary?.swap)} perp
          </span>
          {newListings.length === 0 ? (
            <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", fontStyle: "italic" }}>no new listings this hour</span>
          ) : newListings.slice(0, 12).map(r => (
            <span key={r.symbol} style={{ fontSize: 9, padding: "3px 8px", borderRadius: 6, fontFamily: "var(--font-mono)", background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.4)", color: "#f97316", fontWeight: 700 }}>
              NEW {r.symbol}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ResearchIngestPanel({ papers, macro, min, setMin, theme, setTheme, onBrief }) {
  const THEMES = ["entropy", "sizing", "regime", "derivatives", "postmortem", "execution", "microstructure", "ml"];
  const macroCard = (label, src, key, color) => {
    const s = macro?.[src];
    if (!s) return null;
    const v = s.data?.[key];
    const d = s.deltas?.[key];
    return (
      <div style={{ flex: 1, minWidth: 118, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "8px 10px" }}>
        <div style={{ fontSize: 8.5, letterSpacing: ".12em", color: "rgba(255,255,255,0.4)", textTransform: "uppercase" }}>{label}</div>
        <div style={{ fontSize: 15, fontWeight: 800, color, marginTop: 3 }}>{fmtUsdCompact(v)}</div>
        {d != null && d !== 0 && (
          <div style={{ fontSize: 9, color: d >= 0 ? "#4dffaf" : "#fb7185", marginTop: 1 }}>{d >= 0 ? "▲" : "▼"} {fmtUsdCompact(Math.abs(d))}</div>
        )}
      </div>
    );
  };
  const filtered = (papers || []).filter(p =>
    (p.relevance_score ?? 0) >= min && (!theme || (p.themes || []).includes(theme)));

  return (
    <div className="arena-equity-card">
      <SectionLabel icon={FileText} label="Research Ingest" sub="arXiv q-fin · DefiLlama macro · local-model scored" color="#c4b5fd" count={filtered.length} />
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        {macroCard("DeFi TVL", "chains", "total_tvl", "#5cf2ff")}
        {macroCard("Stablecoin supply", "stablecoins", "total_circulating", "#4dffaf")}
        {macroCard("Fees 24h", "fees", "total24h", "#fbbf24")}
        {macroCard("DEX vol 24h", "dexs", "total24h", "#c4b5fd")}
      </div>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 9, alignItems: "center" }}>
        {[0, 50, 70, 85].map(m => (
          <button key={m} onClick={() => setMin(m)} style={_resChip(min === m)}>{m === 0 ? "ALL" : `${m}+`}</button>
        ))}
        <span style={{ width: 1, height: 14, background: "rgba(255,255,255,0.1)", margin: "0 3px" }} />
        <button onClick={() => setTheme(null)} style={_resChip(!theme)}>all</button>
        {THEMES.map(t => (
          <button key={t} onClick={() => setTheme(theme === t ? null : t)} style={_resChip(theme === t)}>{t}</button>
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {filtered.map((p, i) => {
          const url = paperSourceUrl(p);
          const openPaper = () => { if (url) window.open(url, "_blank", "noopener,noreferrer"); };
          return (
          <div key={p.arxiv_id || i} role="link" tabIndex={0} onClick={openPaper}
             onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); openPaper(); } }}
             style={{ cursor: url ? "pointer" : "default", textDecoration: "none", display: "block", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "9px 11px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4 }}>{p.title}{url && <span style={{ color: "#93c5fd", fontSize: 9, marginLeft: 5 }}>↗</span>}</div>
              <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 800, color: _resScoreColor(p.relevance_score), fontFamily: "var(--font-mono)" }}>{p.relevance_score ?? "—"}</span>
            </div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 5, alignItems: "center" }}>
              {(p.themes || []).map((t, j) => (
                <span key={j} style={{ fontSize: 8.5, padding: "2px 6px", borderRadius: 5, background: "rgba(196,181,253,0.1)", color: "#c4b5fd", letterSpacing: ".04em" }}>{t}</span>
              ))}
              <span style={{ fontSize: 9, color: "rgba(255,255,255,0.32)", marginLeft: "auto" }}>
                {(p.authors || []).slice(0, 2).join(", ")}{(p.authors || []).length > 2 ? " et al." : ""} · {(p.published || "").slice(0, 10)}
              </span>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8, paddingTop: 7, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
              <button onClick={(e) => { e.stopPropagation(); openPaper(); }} style={paperActionStyle(false)}>
                Open paper <ExternalLink style={{ width: 10, height: 10 }} />
              </button>
              {p.arxiv_id && (
                <button onClick={(e) => { e.stopPropagation(); onBrief?.(p); }} style={paperActionStyle(true)}>
                  <Sparkles style={{ width: 10, height: 10 }} /> AEON integration brief
                </button>
              )}
            </div>
          </div>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "16px 0", fontSize: 10, color: "rgba(255,255,255,0.2)", fontStyle: "italic" }}>
            No papers match this filter. arXiv q-fin is ingested every 6h and scored by the local model.
          </div>
        )}
      </div>
    </div>
  );
}

export function PapersPanel({ papers, onBrief, showEmpty = true }) {
  const [showModal, setShowModal] = useState(false);

  const libraryBanner = (
    <div className="rlab-library-banner">
      <div style={{ fontSize: 10, color: "#93c5fd", fontWeight: 700, marginBottom: 3 }}>📚 Personal Research Library</div>
      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.42)", lineHeight: 1.55 }}>
        AEON's internal research paper is pinned above. Each arXiv card opens the original source; “AEON integration brief” builds a full implementation-oriented synthesis and caches it for future review.
      </div>
    </div>
  );

  return (
    <>
      {showModal && <PaperModal onClose={() => setShowModal(false)} />}
      <div className="arena-equity-card">
        <SectionLabel icon={BookOpen} label="Research Papers" sub="AEON Internal · arXiv · q-fin · quant-ph" color="#93c5fd" count={(papers?.length || 0) + 1} />
        {libraryBanner}
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {/* Pinned AEON internal paper */}
          <PaperCard p={AEON_PAPER} onOpen={() => setShowModal(true)} />

          {/* arXiv papers from backend */}
          {(papers || []).map((p, i) => (
            <PaperCard key={p.arxiv_id || i} p={p} onBrief={onBrief} />
          ))}

          {showEmpty && (!papers || papers.length === 0) && (
            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.2)", fontStyle: "italic" }}>
                Researcher scans arXiv q-fin daily. More papers appear as they are discovered.
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Strategies panel ──────────────────────────────────────────────────────────

function StrategiesPanel({ strategies, onOpen }) {
  return (
    <div className="arena-equity-card">
      <SectionLabel icon={Cpu} label="Quantum Strategies" sub="Implemented · Backtested · Wired" color="#c4b5fd" count={strategies?.length} />
      {(!strategies || strategies.length === 0) ? (
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.22)", textAlign: "center", padding: "16px 0" }}>
          No strategies loaded.
        </div>
      ) : strategies.map((s, i) => (
        <div key={i} className={`rlab-strategy-row${s.wired ? " is-wired" : ""}`} style={{ cursor: "pointer" }}
          onClick={() => onOpen && onOpen({ ...s, _kind: "strategy" })}>
          <span style={{
            width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
            background: s.wired ? "#4dffaf" : s.status === "implemented" ? "#93c5fd" : "#ffc24d",
          }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span className="rlab-strategy-name">{s.name}</span>
              {s.wired && <Chip label="WIRED" color="#4dffaf" bg="rgba(77,255,175,0.08)" border="rgba(77,255,175,0.25)" />}
              {s.status && !s.wired && <Chip label={s.status} color="#93c5fd" bg="rgba(147,197,253,0.06)" border="rgba(147,197,253,0.2)" />}
            </div>
            {s.description && <div className="rlab-strategy-desc">{s.description}</div>}
          </div>
          {s.backtest ? (
            <div style={{ textAlign: "right", fontSize: 10, color: "#93c5fd", flexShrink: 0 }}>
              <div>SR {s.backtest.sharpe?.toFixed(2) ?? "—"}</div>
              {s.backtest.win_rate != null && <div>{(s.backtest.win_rate * 100).toFixed(0)}% WR</div>}
            </div>
          ) : (
            <div style={{ fontSize: 9, color: "rgba(255,255,255,0.18)", flexShrink: 0 }}>no backtest</div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Live signals panel ────────────────────────────────────────────────────────

function LiveSignalsPanel({ hurst, ensemble, tcn }) {
  const tcnVote   = tcn?.vote || "NEUTRAL";
  const tcnColors = VOTE_STYLE[tcnVote] || VOTE_STYLE.NEUTRAL;

  return (
    <div className="arena-equity-card">
      <SectionLabel icon={Activity} label="Live Research Signals" sub="Hurst · Ensemble · TCN" color="#5cf2ff" />

      {/* TCN Vote */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12, marginBottom: 16,
        padding: "10px 12px", borderRadius: 9,
        background: tcnColors.bg, border: `1px solid ${tcnColors.border}`,
      }}>
        <Atom style={{ width: 20, height: 20, color: tcnColors.text, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.38)", letterSpacing: "0.12em", marginBottom: 2 }}>TCN NEURAL · BTC/USDT</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: tcnColors.text, letterSpacing: "0.04em" }}>{tcnVote}</div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0", fontFamily: "var(--font-mono)" }}>
            {(tcn?.confidence || 0).toFixed(1)}%
          </div>
          <div style={{ fontSize: 8, color: "rgba(255,255,255,0.28)" }}>confidence</div>
          <div style={{ fontSize: 9, color: tcn?.live ? "#4dffaf" : "#f87171", marginTop: 2, letterSpacing: "0.1em" }}>
            {tcn?.live ? "LIVE" : "STALE"}
          </div>
        </div>
      </div>

      {/* Ensemble */}
      <div style={{ marginBottom: 16 }}>
        <div className="rlab-section-lbl">Ensemble consensus</div>
        {!ensemble || ensemble.length === 0 ? (
          <Empty kind="zero" detail="Consensus needs 3+ engines agreeing inside a 600s window.">
            No symbol had 3+ engines agree in the last 10 min. Working as designed.
          </Empty>
        ) : ensemble.slice(0, 3).map((c, i) => (
          <div key={i} className={`rlab-consensus-row${c.elevated ? " is-elevated" : ""}`}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#e2e8f0", minWidth: 36 }}>
              {c.symbol?.replace("/USDT", "")}
            </span>
            <Chip
              label={c.direction}
              color={c.direction === "long" ? "#4dffaf" : "#ff5577"}
              bg="rgba(0,0,0,0)" border="rgba(255,255,255,0.1)"
            />
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>{c.count} engines</span>
            {c.elevated && <Chip label="ELEVATED" color="#fcd34d" bg="rgba(255,194,77,0.07)" border="rgba(255,194,77,0.25)" />}
          </div>
        ))}
      </div>

      {/* Hurst */}
      <div>
        <div className="rlab-section-lbl">Hurst gate 16</div>
        {!hurst || hurst.length === 0 ? (
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.2)", fontStyle: "italic" }}>Cache populating…</div>
        ) : hurst.slice(0, 6).map((s, i) => {
          const rc = REGIME_STYLE[s.regime] || REGIME_STYLE.UNKNOWN;
          return (
            <div key={i} className="rlab-hurst-row">
              <span className="rlab-hurst-sym">{s.symbol?.replace("/USDT", "")}</span>
              <div className="rlab-hurst-track">
                <div className="rlab-hurst-fill" style={{ width: `${s.hurst * 100}%`, background: rc.text }} />
              </div>
              <span className="rlab-hurst-val" style={{ color: rc.text }}>{s.hurst.toFixed(3)}</span>
              <span className="rlab-regime-tag" style={{ color: rc.text, background: rc.bg, borderColor: rc.border }}>
                {s.regime === "MEAN_REVERSION" ? "MEAN-REV" : s.regime === "RANDOM_WALK" ? "BLOCKED" : s.regime?.slice(0, 5) || "—"}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Calibration panel ─────────────────────────────────────────────────────────

function CalibrationPanel({ calibration, totalTrades, overallWinRate }) {
  return (
    <div className="arena-equity-card">
      <SectionLabel icon={BarChart3} label="Confidence Calibration" sub={`${totalTrades || 0} closed trades · 30d window`} color="#a78bfa" />
      {(!calibration || calibration.length === 0) ? (
        <Empty kind="waiting" detail="No closed paper trades carried a confidence score in the last 30 days.">
          Calibration builds as paper trades close — nothing scored in this window yet.
        </Empty>
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "rgba(255,255,255,0.38)", marginBottom: 10 }}>
            <span>Confidence range</span>
            <span>Overall WR: <strong style={{ color: "#e2e8f0" }}>{overallWinRate}%</strong></span>
          </div>
          {calibration.map((d) => {
            const gap      = d.calibration_gap;
            const barColor = d.win_rate >= 55 ? "#4dffaf" : d.win_rate >= 45 ? "#ffc24d" : "#ff5577";
            const gapColor = Math.abs(gap) < 5 ? "#4dffaf" : gap > 0 ? "#ffc24d" : "#ff5577";
            return (
              <div key={d.range} style={{ marginBottom: 9 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "rgba(255,255,255,0.38)", marginBottom: 3 }}>
                  <span style={{ fontFamily: "var(--font-mono)" }}>{d.range}</span>
                  <div style={{ display: "flex", gap: 10 }}>
                    <span style={{ color: barColor }}>{d.win_rate}% WR</span>
                    <span style={{ color: gapColor }}>{gap > 0 ? "+" : ""}{gap}pp</span>
                    <span>n={d.count}</span>
                  </div>
                </div>
                <div style={{ height: 5, background: "rgba(255,255,255,0.06)", borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.min(100, d.win_rate)}%`, background: barColor, borderRadius: 3, transition: "width 0.4s" }} />
                </div>
              </div>
            );
          })}
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", marginTop: 8 }}>
            Gap = actual WR − expected WR. Positive = under-confident; negative = over-confident.
          </div>
        </>
      )}
    </div>
  );
}

// ── Autonomous Lab panel (strategy scientist) ─────────────────────────────────

// ── Universal research detail modal — everything is clickable ─────────────────

const METRIC_LABELS = {
  score: "Robustness score", sharpe_ratio: "Sharpe", min_sharpe: "Worst-symbol Sharpe",
  win_rate: "Win rate %", total_trades: "Trades", max_drawdown: "Max drawdown %",
  total_return: "Total return %", exposure: "Exposure %",
  symbols_traded: "Symbols traded", symbols_tested: "Symbols tested",
  avg_net_return_pct: "Net return / trade (after fees)", profitable_pairs: "Profitable pairs",
  trades: "Trades", equity: "Equity ($)", balance: "Balance ($)",
  wins: "Wins", losses: "Losses",
};
const humanizeKey = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Candidate pipeline stage → what it means in plain language (mirrors STAGE_META below).
const STAGE_EXPLAINER = {
  intake:     "Just proposed. No backtest pressure applied yet — nothing about its edge is known.",
  testing:    "Under active backtest pressure on real OHLCV. It stays here until it either clears the live-deploy bar or fails and moves to Restricted / the Lessons Vault.",
  live:       "Cleared backtest and is now forward-testing on live prices in an isolated sandbox — the out-of-sample check that catches overfitting a backtest alone would miss.",
  promoted:   "Cleared every bar: backtest, forward-test, and (for tournament ideas) real trading fees. It's live in production or graduated to canary deployment.",
  restricted: "Reverted, blocked, or failed its checks. Kept visible on purpose — see the reason below and the Lessons Vault for the full failure record.",
};

function DetailBlock({ label, children, color = "#7dd3fc" }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", color, marginBottom: 4, textTransform: "uppercase" }}>{label}</div>
      {children}
    </div>
  );
}

function JsonBlock({ data }) {
  return (
    <pre style={{ fontSize: 10, lineHeight: 1.5, color: "rgba(255,255,255,0.7)", background: "rgba(0,0,0,0.3)",
      border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: 10, overflowX: "auto", whiteSpace: "pre-wrap", margin: 0 }}>
      {typeof data === "string" ? data : JSON.stringify(data, null, 2)}
    </pre>
  );
}

function BacktestViz({ item }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null); setData(null);
    (async () => {
      try {
        const body = {
          kind: item.kind || (item.code ? "code" : "vocab"),
          spec: item.spec, code: item.code, params: item.params, symbol: "BTC/USDT",
        };
        const res = await fetch(`${API}/researcher/desk/backtest_viz`, {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
        });
        const d = await res.json();
        if (!alive) return;
        if (d.error) setErr(d.error); else setData(d);
      } catch { if (alive) setErr("backtest failed"); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [item]);

  const W = 100, H = 42, RIB = 5;  // chart coords; ribbon at the bottom
  let eqPath = "", pricePath = "", runs = [];
  if (data?.equity?.length) {
    const eq = data.equity, pr = data.price, pos = data.positions || [];
    const n = eq.length;
    const eMin = Math.min(...eq), eMax = Math.max(...eq);
    const pMin = Math.min(...pr), pMax = Math.max(...pr);
    const eScale = v => (H - RIB) - ((v - eMin) / ((eMax - eMin) || 1)) * (H - RIB - 2) - 1;
    const pScale = v => (H - RIB) - ((v - pMin) / ((pMax - pMin) || 1)) * (H - RIB - 2) - 1;
    const x = i => (i / (n - 1)) * W;
    eqPath = eq.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(2)} ${eScale(v).toFixed(2)}`).join(" ");
    pricePath = pr.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(2)} ${pScale(v).toFixed(2)}`).join(" ");
    // contiguous position runs → ribbon rects
    let s = 0;
    for (let i = 1; i <= n; i++) {
      if (i === n || pos[i] !== pos[s]) {
        if (pos[s] !== 0) runs.push({ x0: x(s), x1: x(i - 1), dir: pos[s] });
        s = i;
      }
    }
  }
  const up = (data?.final_return_pct ?? 0) >= 0;
  const col = up ? "#4dffaf" : "#ff5577";

  return (
    <DetailBlock label="🔬 Blackbox backtest — testing on real BTC/USDT (300h)" color="#7dd3fc">
      {loading ? (
        <div style={{ height: 90, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: "#7dd3fc", fontSize: 11,
          background: "rgba(0,0,0,0.25)", borderRadius: 8, border: "1px solid rgba(125,211,252,0.15)" }}>
          <RefreshCw style={{ width: 13, height: 13, animation: "spin 1s linear infinite" }} />
          running strategy across 300 candles…
        </div>
      ) : err ? (
        <div style={{ fontSize: 10.5, color: "#ff5577", padding: 8, background: "rgba(255,85,119,0.06)", borderRadius: 8 }}>
          Couldn't backtest: {err}
        </div>
      ) : data ? (
        <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 8, border: "1px solid rgba(255,255,255,0.08)", padding: "8px 6px 4px" }}>
          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 150 }} preserveAspectRatio="none">
            {/* position ribbon (what the strategy was doing) */}
            {runs.map((r, i) => (
              <rect key={i} x={r.x0} y={H - RIB} width={Math.max(0.3, r.x1 - r.x0)} height={RIB}
                fill={r.dir > 0 ? "#4dffaf" : "#ff5577"} opacity="0.5" />
            ))}
            {/* faint price */}
            <path d={pricePath} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="0.4" />
            {/* equity curve */}
            <path d={eqPath} fill="none" stroke={col} strokeWidth="0.8" />
          </svg>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 10, marginTop: 4, padding: "0 4px" }}>
            <span style={{ color: col, fontWeight: 700 }}>{up ? "+" : ""}{data.final_return_pct}% equity</span>
            <span style={{ color: "rgba(255,255,255,0.5)" }}>{data.n_trades} trades · {data.wins}W</span>
            <span style={{ color: "rgba(77,255,175,0.7)" }}>▬ long</span>
            <span style={{ color: "rgba(255,85,119,0.7)" }}>▬ short</span>
            <span style={{ color: "rgba(255,255,255,0.3)" }}>▬ equity · faint = price</span>
          </div>
        </div>
      ) : null}
    </DetailBlock>
  );
}

function ResearchDetailModal({ item, onClose }) {
  const [showRaw, setShowRaw] = useState(false);
  if (!item) return null;
  const metrics = item.metrics || item;  // findings nest metrics; strategies are flat
  // Show EVERY populated metric, not just a known whitelist — nothing gets silently dropped.
  const metricRows = Object.entries(metrics)
    .filter(([k, v]) => v !== null && v !== undefined && typeof v !== "object" && k !== "id" && k !== "_id")
    .map(([k, v]) => [k, METRIC_LABELS[k] || humanizeKey(k), v]);
  const title = item.title || item.name || item.type || "Detail";
  const when = item.at || item.discovered_at || item.created_at;
  const verdictColor = item.verdict === "survived" ? "#34d399" : item.verdict === "rejected" ? "#f87171" : "#fbbf24";
  const stageMeta = item.stage ? STAGE_META[item.stage] : null;
  const hasContext = item.market || item.source || item.sample_count != null || item.last_result
    || item.regime_fit || item.next_decision || item.updated_at || item.stage;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.7)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 560, maxHeight: "86vh", overflowY: "auto",
        background: "#0c1120", border: "1px solid rgba(125,211,252,0.25)", borderRadius: 16, padding: 18 }}>
        {/* header */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#e8eefc", lineHeight: 1.3 }}>{title}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 5, alignItems: "center" }}>
              {item.author && <span style={{ fontSize: 10, color: "#c4b5fd", fontWeight: 700 }}>{item.author}</span>}
              {item.kind && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 5, background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.5)" }}>{item.kind}</span>}
              {item.verdict && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 7px", borderRadius: 5, background: `${verdictColor}1f`, color: verdictColor }}>{item.verdict}</span>}
              {item.status && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 7px", borderRadius: 5, background: "rgba(52,211,153,0.12)", color: "#34d399" }}>{item.status}</span>}
              {when && <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)" }}>{new Date(when).toLocaleString()}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.5)", padding: 2 }}>
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {(item.spec || item.code) && <BacktestViz item={item} />}

        {stageMeta && (
          <DetailBlock label={`Pipeline stage — ${stageMeta.label}`} color={stageMeta.color}>
            <div style={{ fontSize: 11.5, lineHeight: 1.6, color: "rgba(255,255,255,0.72)" }}>
              {STAGE_EXPLAINER[item.stage] || stageMeta.hint}
            </div>
          </DetailBlock>
        )}

        {item.thesis
          ? <DetailBlock label="Thesis — why this edge should exist" color="#fbbf24"><div style={{ fontSize: 12, lineHeight: 1.6, color: "rgba(255,255,255,0.8)" }}>{item.thesis}</div></DetailBlock>
          : (item.source || item.author) && (
            <DetailBlock label="What this is" color="#7dd3fc">
              <div style={{ fontSize: 11.5, lineHeight: 1.6, color: "rgba(255,255,255,0.65)" }}>
                No written thesis on file — this candidate came from {item.source ? `the ${item.source}` : "the research org"}
                {item.author ? `, credited to ${item.author}` : ""}. Everything actually known about it is below.
              </div>
            </DetailBlock>
          )
        }
        {item.reason && <DetailBlock label="Verdict reason" color={verdictColor}><div style={{ fontSize: 11.5, lineHeight: 1.55, color: "rgba(255,255,255,0.7)" }}>{item.reason}</div></DetailBlock>}
        {item.rationale && <DetailBlock label="Rationale"><div style={{ fontSize: 11.5, lineHeight: 1.55, color: "rgba(255,255,255,0.7)" }}>{item.rationale}</div></DetailBlock>}

        {hasContext && (
          <DetailBlock label="Context" color="#93c5fd">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {item.source != null && (
                <div style={{ fontSize: 11, padding: "4px 8px", background: "rgba(255,255,255,0.03)", borderRadius: 6 }}>
                  <span style={{ color: "rgba(255,255,255,0.45)" }}>Source: </span>
                  <span style={{ color: "#e8eefc", fontWeight: 600 }}>{item.source}</span>
                </div>
              )}
              {item.market != null && (
                <div style={{ fontSize: 11, padding: "4px 8px", background: "rgba(255,255,255,0.03)", borderRadius: 6 }}>
                  <span style={{ color: "rgba(255,255,255,0.45)" }}>Market: </span>
                  <span style={{ color: "#e8eefc", fontWeight: 600 }}>{item.market}</span>
                </div>
              )}
              {item.sample_count != null && (
                <div style={{ fontSize: 11, padding: "4px 8px", background: "rgba(255,255,255,0.03)", borderRadius: 6 }}>
                  <span style={{ color: "rgba(255,255,255,0.45)" }}>Sample size: </span>
                  <span style={{ color: "#e8eefc", fontWeight: 600, fontFamily: "var(--font-mono)" }}>{item.sample_count} trades</span>
                </div>
              )}
              {item.regime_fit != null && (
                <div style={{ fontSize: 11, padding: "4px 8px", background: "rgba(255,255,255,0.03)", borderRadius: 6 }}>
                  <span style={{ color: "rgba(255,255,255,0.45)" }}>Best regime: </span>
                  <span style={{ color: "#e8eefc", fontWeight: 600 }}>{item.regime_fit}</span>
                </div>
              )}
              {item.updated_at != null && (
                <div style={{ fontSize: 11, padding: "4px 8px", background: "rgba(255,255,255,0.03)", borderRadius: 6 }}>
                  <span style={{ color: "rgba(255,255,255,0.45)" }}>Last updated: </span>
                  <span style={{ color: "#e8eefc", fontWeight: 600 }}>{new Date(item.updated_at).toLocaleString()}</span>
                </div>
              )}
              {item.next_decision != null && (
                <div style={{ fontSize: 11, padding: "4px 8px", background: "rgba(255,255,255,0.03)", borderRadius: 6, gridColumn: "1 / -1" }}>
                  <span style={{ color: "rgba(255,255,255,0.45)" }}>Next decision: </span>
                  <span style={{ color: "#e8eefc", fontWeight: 600 }}>{item.next_decision}</span>
                </div>
              )}
            </div>
            {item.last_result && (
              <div style={{ fontSize: 11.5, marginTop: 8, padding: "7px 9px", background: "rgba(77,255,175,0.05)", border: "1px solid rgba(77,255,175,0.15)", borderRadius: 7, color: "#e8eefc", fontFamily: "var(--font-mono)" }}>
                {item.last_result}
              </div>
            )}
          </DetailBlock>
        )}

        {typeof item.confidence === "number" && (
          <DetailBlock label="Confidence" color="#fbbf24">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1, height: 6, borderRadius: 6, background: "rgba(255,255,255,0.08)" }}>
                <div style={{ width: `${Math.round(item.confidence * 100)}%`, height: 6, borderRadius: 6, background: "#fbbf24" }} />
              </div>
              <span style={{ fontSize: 11, color: "#fbbf24", fontWeight: 700 }}>{Math.round(item.confidence * 100)}%</span>
            </div>
          </DetailBlock>
        )}

        {metricRows.length > 0 && (
          <DetailBlock label="Metrics — every number on record" color="#4dffaf">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {metricRows.map(([k, lbl, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, padding: "4px 8px", background: "rgba(255,255,255,0.03)", borderRadius: 6 }}>
                  <span style={{ color: "rgba(255,255,255,0.45)" }}>{lbl}</span>
                  <span style={{ color: "#e8eefc", fontWeight: 600, fontFamily: "var(--font-mono)" }}>{String(v)}</span>
                </div>
              ))}
            </div>
          </DetailBlock>
        )}

        {item.code && <DetailBlock label="Authored strategy code (sandboxed)" color="#93c5fd"><JsonBlock data={item.code} /></DetailBlock>}
        {item.spec && <DetailBlock label="Strategy spec (signal vocabulary)" color="#93c5fd"><JsonBlock data={item.spec} /></DetailBlock>}
        {item.params && <DetailBlock label="Parameters"><JsonBlock data={item.params} /></DetailBlock>}
        {item.before_state && <DetailBlock label="Before-state (for revert)"><JsonBlock data={item.before_state} /></DetailBlock>}
        {item.result && <DetailBlock label="Result"><JsonBlock data={item.result} /></DetailBlock>}

        {Array.isArray(item.evidence) && item.evidence.length > 0 && (
          <DetailBlock label={`Evidence — ${item.evidence.length} corroborating experiment(s)`} color="#34d399">
            {item.evidence.map((e, i) => (
              <div key={i} style={{ fontSize: 10.5, color: "rgba(255,255,255,0.6)", padding: "4px 8px", background: "rgba(52,211,153,0.05)", borderRadius: 6, marginBottom: 4 }}>
                score {e.score} · Sharpe {e.sharpe} · {e.at ? new Date(e.at).toLocaleString() : ""}
              </div>
            ))}
          </DetailBlock>
        )}

        {/* Nothing held back — every field this item actually has, verbatim */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", marginTop: 4, paddingTop: 10 }}>
          <button onClick={() => setShowRaw(s => !s)} style={{
            display: "flex", alignItems: "center", gap: 6, width: "100%", background: "none", border: "none",
            cursor: "pointer", padding: 0, color: "rgba(255,255,255,0.4)", fontSize: 9, fontWeight: 700,
            letterSpacing: "0.1em", textTransform: "uppercase",
          }}>
            <ChevronUp style={{ width: 11, height: 11, transform: showRaw ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 160ms ease" }} />
            {showRaw ? "Hide raw record" : "Show raw record — everything stored for this item"}
          </button>
          {showRaw && <div style={{ marginTop: 8 }}><JsonBlock data={item} /></div>}
        </div>
      </div>
    </div>
  );
}

// ── Lab Visual + Explainer (the autonomous scientist, illustrated) ────────────

function MiniStat({ label, value, color }) {
  return (
    <div style={{ flex: 1, minWidth: 78, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, padding: "8px 10px" }}>
      <div style={{ fontSize: 17, fontWeight: 800, color, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 8.5, color: "rgba(255,255,255,0.4)", letterSpacing: "0.08em", marginTop: 2 }}>{label}</div>
    </div>
  );
}

function LabVisualPanel({ lab, leaderboard, onOpen }) {
  const champ = lab?.champion;
  const sb = lab?.sandbox;
  const board = (leaderboard || []).slice(0, 8);
  const maxScore = Math.max(1, ...board.map(s => s.score || 0));
  const liveWR = sb?.win_rate;
  const btWR = champ?.win_rate;

  return (
    <div className="arena-equity-card">
      <SectionLabel icon={BarChart3} label="The Lab, Visualized" sub="What the scientist is doing & finding" color="#4dffaf" />

      {/* Plain-language explainer */}
      <div style={{ fontSize: 10.5, lineHeight: 1.6, color: "rgba(255,255,255,0.55)", marginBottom: 12, background: "rgba(77,255,175,0.04)", border: "1px solid rgba(77,255,175,0.15)", borderRadius: 8, padding: "9px 11px" }}>
        <strong style={{ color: "#4dffaf" }}>What you're looking at:</strong> every cycle the Lab invents
        trading strategies, backtests them on real BTC/ETH/SOL/BNB price history, scores them for
        robustness, and crowns a champion — then forward-tests that champion on live price to check the
        edge is real, not just curve-fit.
      </div>

      {/* KPI tiles */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        <MiniStat label="CYCLES RUN" value={lab?.cycles ?? 0} color="#93c5fd" />
        <MiniStat label="STRATEGIES TESTED" value={(lab?.total_evaluated ?? 0).toLocaleString()} color="#c4b5fd" />
        <MiniStat label="CHAMPION SHARPE" value={champ ? champ.sharpe_ratio : "—"} color="#4dffaf" />
        <MiniStat label="CHAMPION WIN%" value={champ ? `${Math.round(champ.win_rate)}%` : "—"} color="#4dffaf" />
      </div>

      {/* Leaderboard bar chart */}
      <div className="rlab-section-lbl">Leaderboard — robustness score (taller = better edge)</div>
      <div style={{ marginTop: 8, marginBottom: 6 }}>
        {board.length === 0 ? (
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", fontStyle: "italic" }}>First cycle runs shortly after boot…</div>
        ) : board.map((s, i) => {
          const w = Math.max(4, Math.round((s.score / maxScore) * 100));
          const isChamp = champ && s.name === champ.name;
          const col = isChamp ? "#ffc24d" : (s.template === "mean_reversion" ? "#4dffaf" : "#5b8cff");
          return (
            <div key={i} onClick={() => onOpen && onOpen({ ...s, _kind: "strategy" })}
              style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, cursor: "pointer" }}>
              <span style={{ width: 118, flexShrink: 0, fontSize: 9, color: "rgba(255,255,255,0.6)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {isChamp ? "🏆 " : ""}{s.name}
              </span>
              <div style={{ flex: 1, height: 14, background: "rgba(255,255,255,0.04)", borderRadius: 4, overflow: "hidden" }}>
                <div style={{ width: `${w}%`, height: "100%", background: col, borderRadius: 4, transition: "width .4s" }} />
              </div>
              <span style={{ width: 34, textAlign: "right", fontSize: 9.5, fontWeight: 700, color: col, fontFamily: "var(--font-mono)" }}>{s.score}</span>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.35)", marginBottom: 14 }}>
        Each bar is one strategy. Gold = current champion · green = mean-reversion family · blue = others.
      </div>

      {/* Backtest vs Live reality check */}
      <div className="rlab-section-lbl">Reality check — backtest vs live forward-test</div>
      {(btWR == null) ? (
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", fontStyle: "italic", marginTop: 6 }}>No champion crowned yet.</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 16, alignItems: "flex-end", height: 96, marginTop: 10, padding: "0 8px" }}>
            {[{ lbl: "Backtest WR", v: btWR, c: "#4dffaf" }, { lbl: "LIVE WR", v: liveWR ?? 0, c: (liveWR ?? 0) >= (btWR * 0.7) ? "#4dffaf" : "#ff5577" }].map((b, i) => (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%" }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: b.c, marginBottom: 4 }}>{Math.round(b.v)}%</div>
                <div style={{ width: "62%", maxWidth: 90, height: `${Math.max(3, b.v)}%`, background: b.c, borderRadius: "5px 5px 0 0", minHeight: 4 }} />
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.45)", marginTop: 6 }}>{b.lbl}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.4)", marginTop: 10, lineHeight: 1.5 }}>
            {sb?.closed_trades ? (
              (liveWR ?? 0) >= (btWR * 0.7)
                ? "✅ The edge is holding up out-of-sample so far."
                : `⚠️ The champion looks strong in backtest (${Math.round(btWR)}%) but is underperforming live (${Math.round(liveWR ?? 0)}% over ${sb.closed_trades} trades) — the classic overfitting warning. This is exactly why the Lab forward-tests instead of trusting backtests.`
            ) : "Champion just deployed — no live trades closed yet. Watch this gap."}
          </div>
        </>
      )}
    </div>
  );
}

function LabDiscoveryPanel({ lab, leaderboard, discoveries, onRun, running, onOpen }) {
  const champ = lab?.champion;
  // Was a local implementation over `new Date(iso)`, which reads an offset-less
  // API timestamp as LOCAL time and rendered "-5848s ago". Shared `ago()` treats
  // offset-less strings as UTC and clamps at zero.
  const fmtAgo = ago;

  return (
    <div className="arena-equity-card">
      <SectionLabel
        icon={FlaskConical}
        label="Strategy Lab — Autonomous Scientist"
        sub="Generates · backtests · scores candidates on real OHLCV"
        color="#4dffaf"
        count={lab?.running ? "LIVE" : "IDLE"}
      />

      <StatGrid items={[
        { l: "Cycles",     v: lab?.cycles ?? 0,                 color: "#e2e8f0" },
        { l: "Evaluated",  v: lab?.total_evaluated ?? 0,        color: "#93c5fd" },
        { l: "On Board",   v: lab?.leaderboard_size ?? 0,       color: "#c4b5fd" },
        { l: "Last Run",   v: fmtAgo(lab?.last_run),            color: "#ffc24d" },
      ]} />

      {/* Champion banner */}
      {champ ? (
        <div style={{ background: "rgba(77,255,175,0.06)", border: "1px solid rgba(77,255,175,0.22)", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
          <div style={{ fontSize: 9, color: "#4dffaf", fontWeight: 800, letterSpacing: "0.14em", marginBottom: 4 }}>🏆 CHAMPION — clears live-deploy bar</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#e2e8f0", marginBottom: 4 }}>{champ.name}</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 10, color: "rgba(255,255,255,0.5)" }}>
            <span>SR <strong style={{ color: "#4dffaf" }}>{champ.sharpe_ratio}</strong></span>
            <span>WR <strong style={{ color: "#e2e8f0" }}>{champ.win_rate}%</strong></span>
            <span>{champ.total_trades} trades</span>
            <span>DD <strong style={{ color: "#ff5577" }}>{champ.max_drawdown}%</strong></span>
            <span>{champ.symbols_traded} symbols</span>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", marginBottom: 12, fontStyle: "italic" }}>
          No candidate has cleared the live-deploy bar yet (SR ≥ 1.2, WR ≥ 50%, ≥ 20 trades, multi-symbol). Hunt continues…
        </div>
      )}

      {/* Sandbox forward-test */}
      {lab?.sandbox?.active && (
        <div style={{ background: "rgba(92,242,255,0.05)", border: "1px solid rgba(92,242,255,0.2)", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 9, color: "#5cf2ff", fontWeight: 800, letterSpacing: "0.14em" }}>🚀 LIVE FORWARD-TEST · {lab.sandbox.symbol}</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: (lab.sandbox.return_pct ?? 0) >= 0 ? "#4dffaf" : "#ff5577", fontFamily: "var(--font-mono)" }}>
              {(lab.sandbox.return_pct ?? 0) >= 0 ? "+" : ""}{lab.sandbox.return_pct}%
            </span>
          </div>
          <div style={{ fontSize: 11, color: "#e2e8f0", marginBottom: 4 }}>{lab.sandbox.active.name}</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 10, color: "rgba(255,255,255,0.5)" }}>
            <span>Equity <strong style={{ color: "#e2e8f0", fontFamily: "var(--font-mono)" }}>${lab.sandbox.equity}</strong></span>
            <span>Start ${lab.sandbox.start}</span>
            <span>{lab.sandbox.closed_trades} trades</span>
            <span>WR {lab.sandbox.win_rate}%</span>
            {lab.sandbox.open_position && (
              <span style={{ color: lab.sandbox.open_position.dir > 0 ? "#4dffaf" : "#ff5577" }}>
                {lab.sandbox.open_position.dir > 0 ? "LONG" : "SHORT"} @ {lab.sandbox.open_position.entry_price}
              </span>
            )}
          </div>
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.25)", marginTop: 4 }}>
            Out-of-sample paper book — proves the champion forward, not just in backtest.
          </div>
        </div>
      )}

      {/* Leaderboard */}
      <div className="rlab-section-lbl">Leaderboard — best discovered</div>
      {(!leaderboard || leaderboard.length === 0) ? (
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", fontStyle: "italic", padding: "8px 0" }}>
          No Lab leaderboard entries returned. Either no research cycle has
          scored a candidate yet, or the leaderboard feed did not load — check
          the connection count in the masthead.
        </div>
      ) : leaderboard.slice(0, 8).map((s, i) => (
        <div key={s.signature || i} className="rlab-strategy-row" style={{ cursor: "pointer" }}
          onClick={() => onOpen && onOpen({ ...s, _kind: "strategy" })}>
          <span style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "rgba(255,255,255,0.3)", width: 16, flexShrink: 0 }}>{i + 1}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span className="rlab-strategy-name">{s.name}</span>
            <div className="rlab-strategy-desc">
              {s.symbols_traded}/{s.symbols_tested} symbols · {s.total_trades} trades · exp {s.exposure}%
            </div>
          </div>
          <div style={{ textAlign: "right", fontSize: 10, flexShrink: 0 }}>
            <div style={{ color: "#4dffaf", fontWeight: 700 }}>{s.score}</div>
            <div style={{ color: "rgba(255,255,255,0.4)" }}>SR {s.sharpe_ratio} · {s.win_rate}%</div>
          </div>
        </div>
      ))}

      {/* Discovery feed */}
      {Array.isArray(discoveries) && discoveries.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="rlab-section-lbl">Discovery feed</div>
          {discoveries.slice(0, 5).map((d, i) => (
            <div key={i} onClick={() => onOpen && onOpen({ ...d, _kind: "discovery" })}
              style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 10, color: "rgba(255,255,255,0.5)", marginBottom: 4, cursor: "pointer" }}>
              <Chip
                label={d.type === "champion" ? "🏆 CHAMP" : d.type === "promotion" ? "🏅 PROMO" : d.type === "improved" ? "▲ IMPROVED" : "NEW"}
                color={d.type === "champion" ? "#4dffaf" : d.type === "promotion" ? "#fbbf24" : d.type === "improved" ? "#c4b5fd" : "#93c5fd"}
                bg={d.type === "champion" ? "rgba(77,255,175,0.08)" : d.type === "promotion" ? "rgba(251,191,36,0.08)" : d.type === "improved" ? "rgba(196,181,253,0.07)" : "rgba(147,197,253,0.06)"}
                border={d.type === "champion" ? "rgba(77,255,175,0.25)" : d.type === "promotion" ? "rgba(251,191,36,0.25)" : d.type === "improved" ? "rgba(196,181,253,0.22)" : "rgba(147,197,253,0.2)"}
              />
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</span>
              <span style={{ color: "#4dffaf", flexShrink: 0 }}>{d.score}</span>
              <span style={{ color: "rgba(255,255,255,0.25)", flexShrink: 0 }}>{fmtAgo(d.at)}</span>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={onRun}
        disabled={running}
        style={{
          marginTop: 14, width: "100%", padding: "8px 0", borderRadius: 8,
          border: "1px solid rgba(77,255,175,0.3)", cursor: running ? "wait" : "pointer",
          background: "rgba(77,255,175,0.07)", color: "#4dffaf",
          fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
          opacity: running ? 0.5 : 1,
        }}
      >
        {running ? "RUNNING CYCLE…" : "▶ RUN RESEARCH CYCLE NOW"}
      </button>
    </div>
  );
}

// ── Autonomous Research Desk panel (the quant org) ────────────────────────────

const VERDICT_COLOR = {
  survived: { c: "#34d399", bg: "rgba(52,211,153,0.12)" },
  rejected: { c: "#f87171", bg: "rgba(248,113,113,0.10)" },
};

const ACTION_STATUS_COLOR = {
  executed: "#34d399", pending: "#fbbf24", refused: "#f87171",
  rejected: "#94a3b8", reverted: "#a78bfa", error: "#f87171",
};

function DeskPanel({ desk, findings, theories, agenda, sandbox, actions, onRun, onToggle, onActToggle, onActItem, onOpen, running }) {
  const enabled = desk?.enabled;
  const sb = sandbox || desk?.sandbox;
  const note = agenda?.[0]?.note;
  const act = desk?.act;
  const actOn = act?.act_enabled;
  return (
    <div className="space-y-3">
      {/* Header / controls */}
      <div className="rlab-card" style={{ padding: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <Atom style={{ width: 16, height: 16, color: "#7dd3fc" }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>Autonomous Research Desk</div>
            <div style={{ fontSize: 9, color: "rgba(255,255,255,0.35)", letterSpacing: "0.1em", fontFamily: "var(--font-mono)" }}>
              SIMONS-STYLE · HYPOTHESIS → TEST → KEEP SURVIVORS
            </div>
          </div>
          <button onClick={onToggle}
            style={{ fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 8, cursor: "pointer",
              background: enabled ? "rgba(52,211,153,0.15)" : "rgba(248,113,113,0.15)",
              color: enabled ? "#34d399" : "#f87171",
              border: `1px solid ${enabled ? "rgba(52,211,153,0.3)" : "rgba(248,113,113,0.3)"}` }}>
            {enabled ? "● LIVE — kill" : "○ PAUSED — start"}
          </button>
          <button onClick={onRun} disabled={running}
            style={{ fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 8, cursor: "pointer",
              background: "rgba(125,211,252,0.12)", color: "#7dd3fc", border: "1px solid rgba(125,211,252,0.3)" }}>
            {running ? "Running…" : "Run cycle"}
          </button>
        </div>
        <StatGrid items={[
          { label: "Cycles", value: desk?.cycles ?? 0 },
          { label: "Proposals", value: desk?.total_proposals ?? 0 },
          { label: "Survived", value: desk?.total_survived ?? 0 },
          { label: "Next up", value: desk?.next_researcher || "—" },
        ]} />
      </div>

      {/* ACT mode — the real-action layer */}
      <div className="rlab-card" style={{ padding: 12, borderColor: actOn ? "rgba(248,113,113,0.35)" : "rgba(255,255,255,0.08)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: actOn ? "#f87171" : "#94a3b8" }}>
            ⚡ ACT MODE — {actOn ? "LIVE (auto-acting within bounds)" : "OFF (proposals only)"}
          </span>
          {act?.act_tripped && (
            <span style={{ fontSize: 9, fontWeight: 700, color: "#f87171", padding: "1px 6px", borderRadius: 6, background: "rgba(248,113,113,0.15)" }}>
              CIRCUIT BREAKER TRIPPED
            </span>
          )}
          <button onClick={onActToggle}
            style={{ marginLeft: "auto", fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 8, cursor: "pointer",
              background: actOn ? "rgba(248,113,113,0.15)" : "rgba(52,211,153,0.12)",
              color: actOn ? "#f87171" : "#34d399",
              border: `1px solid ${actOn ? "rgba(248,113,113,0.3)" : "rgba(52,211,153,0.3)"}` }}>
            {actOn ? "Disable ACT (kill)" : "Enable ACT"}
          </button>
        </div>
        <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.4)", marginTop: 5, lineHeight: 1.5 }}>
          When ON, the desk auto-executes only bounded, reversible actions (pause/resume engines, gates,
          risk/leverage within ceilings, canary deploys). Out-of-bounds actions are refused. A drawdown
          circuit breaker auto-reverts and disables ACT if the portfolio drops past the threshold.
        </div>
      </div>

      {/* Head of Research agenda */}
      {note && (
        <div className="rlab-card" style={{ padding: 12, borderColor: "rgba(125,211,252,0.2)" }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#7dd3fc", letterSpacing: "0.1em", marginBottom: 5 }}>
            🧭 HEAD OF RESEARCH — agenda
          </div>
          <div style={{ fontSize: 11, lineHeight: 1.55, color: "rgba(255,255,255,0.72)", whiteSpace: "pre-wrap" }}>{note}</div>
        </div>
      )}

      {/* Forward-test sandbox */}
      {sb?.active && (
        <div className="rlab-card" style={{ padding: 12 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#a78bfa", letterSpacing: "0.1em", marginBottom: 6 }}>
            🚀 LIVE FORWARD-TEST — auto-deployed survivor
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#e2e8f0", marginBottom: 4 }}>
            {sb.active.title} <span style={{ fontSize: 9, color: "rgba(255,255,255,0.4)" }}>({sb.active.kind} · by {sb.active.author})</span>
          </div>
          <StatGrid items={[
            { label: "Equity", value: `$${sb.equity}` },
            { label: "Return", value: `${sb.return_pct > 0 ? "+" : ""}${sb.return_pct}%` },
            { label: "Win rate", value: `${sb.win_rate}%` },
            { label: "Trades", value: sb.closed_trades },
          ]} />
        </div>
      )}

      {/* Theory knowledge base */}
      {theories?.length > 0 && (
        <div className="rlab-card" style={{ padding: 12 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#fbbf24", letterSpacing: "0.1em", marginBottom: 8 }}>
            📚 THEORY KNOWLEDGE BASE — what survived scrutiny
          </div>
          <div className="space-y-2">
            {theories.slice(0, 8).map((t, i) => (
              <div key={i} onClick={() => onOpen && onOpen({ ...t, _kind: "theory" })}
                style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: "#e2e8f0", fontWeight: 600 }}>{t.title}</div>
                  <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.4)" }}>{t.author} · {t.status}</div>
                </div>
                <div style={{ width: 60, height: 4, borderRadius: 4, background: "rgba(255,255,255,0.08)", flexShrink: 0 }}>
                  <div style={{ width: `${Math.round((t.confidence || 0) * 100)}%`, height: 4, borderRadius: 4, background: "#fbbf24" }} />
                </div>
                <span style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", width: 28, textAlign: "right" }}>
                  {Math.round((t.confidence || 0) * 100)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Experiment feed */}
      <div className="rlab-card" style={{ padding: 12 }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: "#93c5fd", letterSpacing: "0.1em", marginBottom: 8 }}>
          🔬 EXPERIMENT FEED — every hypothesis tested
        </div>
        {(!findings || findings.length === 0) ? (
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)" }}>No experiments yet — hit “Run cycle”.</div>
        ) : (
          <div className="space-y-2">
            {findings.slice(0, 20).map((f, i) => {
              const v = VERDICT_COLOR[f.verdict] || VERDICT_COLOR.rejected;
              return (
                <div key={i} onClick={() => onOpen && onOpen({ ...f, _kind: "finding" })}
                  style={{ padding: 8, borderRadius: 8, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", cursor: "pointer" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, color: "#c4b5fd" }}>{f.author}</span>
                    <span style={{ fontSize: 8.5, padding: "1px 5px", borderRadius: 5, background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.5)" }}>{f.kind}</span>
                    <span style={{ marginLeft: "auto", fontSize: 8.5, fontWeight: 700, padding: "1px 6px", borderRadius: 5, background: v.bg, color: v.c }}>
                      {f.verdict}{f.score != null ? ` · ${f.score}` : ""}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: "#e2e8f0", fontWeight: 600 }}>{f.title}</div>
                  <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.45)", lineHeight: 1.45, marginTop: 2 }}>{f.thesis}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Action audit trail */}
      {actions?.length > 0 && (
        <div className="rlab-card" style={{ padding: 12 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#f87171", letterSpacing: "0.1em", marginBottom: 8 }}>
            ⚡ ACTION LOG — real changes to AEON (reversible)
          </div>
          <div className="space-y-2">
            {actions.slice(0, 15).map((a, i) => {
              const col = ACTION_STATUS_COLOR[a.status] || "#94a3b8";
              const p = a.params || {};
              const label = { pause_engine: `pause ${p.engine}`, resume_engine: `resume ${p.engine}`,
                set_gate: `gate ${p.gate_type}`, adjust_risk: `risk ${p.engine}→${p.risk_pct}%`,
                adjust_leverage: `leverage ${p.engine}→${p.leverage}x`, deploy_strategy: `deploy → canary` }[a.type] || a.type;
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: 7, borderRadius: 8, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
                  <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => onOpen && onOpen({ ...a, _kind: "action" })}>
                    <div style={{ fontSize: 11, color: "#e2e8f0", fontWeight: 600 }}>{label}</div>
                    <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)" }}>{a.proposed_by} · {(a.rationale || "").slice(0, 80)}</div>
                  </div>
                  <span style={{ fontSize: 9, fontWeight: 700, color: col, padding: "1px 6px", borderRadius: 5, background: `${col}1f`, flexShrink: 0 }}>{a.status}</span>
                  {a.status === "executed" && (
                    <button onClick={() => onActItem(a.action_id, "revert")} style={{ fontSize: 9, fontWeight: 700, color: "#a78bfa", cursor: "pointer", background: "none", border: "1px solid rgba(167,139,250,0.3)", borderRadius: 6, padding: "2px 6px" }}>revert</button>
                  )}
                  {a.status === "pending" && (
                    <>
                      <button onClick={() => onActItem(a.action_id, "approve")} style={{ fontSize: 9, fontWeight: 700, color: "#34d399", cursor: "pointer", background: "none", border: "1px solid rgba(52,211,153,0.3)", borderRadius: 6, padding: "2px 6px" }}>approve</button>
                      <button onClick={() => onActItem(a.action_id, "reject")} style={{ fontSize: 9, fontWeight: 700, color: "#f87171", cursor: "pointer", background: "none", border: "1px solid rgba(248,113,113,0.3)", borderRadius: 6, padding: "2px 6px" }}>reject</button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

// ── Research Trading — forward-test book (backtest vs live) ──────────────────
function ForwardTradingPanel({ data }) {
  const rows = data?.strategies || [];
  const s = data?.summary || {};
  const bal = data?.account?.balance;
  const money = (n) => `${Number(n || 0) >= 0 ? "+" : "−"}$${Math.abs(Number(n || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const badge = (v) => v === "survives" ? ["#4ade80", "SURVIVES"] : v === "fails-forward" ? ["#ff5d6c", "FAILS FWD"] : ["rgba(255,255,255,0.35)", "no backtest"];
  if (!rows.length) {
    return <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", padding: 12 }}>No forward-test trades yet — the lab book warms up as strategies get promoted and start trading live.</div>;
  }
  return (
    <div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 10, fontSize: 11 }}>
        <span style={{ color: "rgba(255,255,255,0.5)" }}>Book <b style={{ color: "#fff" }}>${Number(bal || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</b></span>
        <span style={{ color: "rgba(255,255,255,0.5)" }}>Forward net <b style={{ color: (s.total_forward_net || 0) >= 0 ? "#4ade80" : "#ff5d6c" }}>{money(s.total_forward_net)}</b></span>
        <span style={{ color: "#4ade80" }}>{s.surviving || 0} survive</span>
        <span style={{ color: "#ff5d6c" }}>{s.failing_forward || 0} fail forward</span>
        <span style={{ color: "rgba(255,255,255,0.4)" }}>· isolated from the main desk</span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr style={{ color: "rgba(255,255,255,0.4)", textAlign: "left", fontSize: 9, textTransform: "uppercase", letterSpacing: ".06em" }}>
              <th style={{ padding: "4px 6px" }}>Strategy</th>
              <th style={{ padding: "4px 6px", textAlign: "right" }}>Backtest WR</th>
              <th style={{ padding: "4px 6px", textAlign: "right" }}>Fwd trades</th>
              <th style={{ padding: "4px 6px", textAlign: "right" }}>Fwd WR</th>
              <th style={{ padding: "4px 6px", textAlign: "right" }}>Forward net</th>
              <th style={{ padding: "4px 6px" }}>Verdict</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const bt = r.backtest; const f = r.forward || {}; const [bc, bl] = badge(r.verdict);
              return (
                <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding: "5px 6px", color: "#fff", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.strategy}</td>
                  <td style={{ padding: "5px 6px", textAlign: "right", color: "rgba(255,255,255,0.55)" }}>{bt && bt.win_rate != null ? `${bt.win_rate}%` : "—"}</td>
                  <td style={{ padding: "5px 6px", textAlign: "right", color: "rgba(255,255,255,0.6)" }}>{f.trades ?? 0}</td>
                  <td style={{ padding: "5px 6px", textAlign: "right", color: "rgba(255,255,255,0.75)" }}>{f.win_rate != null ? `${f.win_rate}%` : "—"}</td>
                  <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 700, color: (f.net_pnl || 0) >= 0 ? "#4ade80" : "#ff5d6c" }}>{money(f.net_pnl)}</td>
                  <td style={{ padding: "5px 6px" }}><span style={{ color: bc, fontSize: 9, fontWeight: 700, letterSpacing: ".04em" }}>{bl}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Quantum Research Tournament panel ─────────────────────────────────────────
function netColor(v) {
  if (v > 0.02) return "#4dffaf";
  if (v < -0.02) return "#ff6b6b";
  return "rgba(255,255,255,0.5)";
}

function SourceBadge({ source }) {
  const isR = source === "research";
  return (
    <span style={{
      fontSize: 8, fontWeight: 700, letterSpacing: "0.08em", padding: "1px 5px", borderRadius: 4,
      textTransform: "uppercase", whiteSpace: "nowrap",
      color: isR ? "#c4b5fd" : "rgba(255,255,255,0.45)",
      background: isR ? "rgba(196,181,253,0.10)" : "rgba(255,255,255,0.04)",
      border: `1px solid ${isR ? "rgba(196,181,253,0.30)" : "rgba(255,255,255,0.10)"}`,
    }}>
      {isR ? "research" : "baseline"}
    </span>
  );
}

function TournamentPanel({ t, promotions, onOpen }) {
  if (!t || !t.ok) {
    return (
      <div className="rlab-intro-banner" style={{ color: "rgba(255,255,255,0.4)", fontSize: 11 }}>
        <RefreshCw style={{ width: 12, height: 12, display: "inline", marginRight: 6 }} />
        Tournament warming up — books need ~10 min of live ticks before they trade.
      </div>
    );
  }
  const board = t.strategy_board || [];
  const combos = t.top_combos || [];
  const promoted = new Set(t.promoted || []);
  const stat = (label, val, color = "#e2e8f0") => (
    <div style={{ flex: "1 1 80px", minWidth: 80 }}>
      <div style={{ fontSize: 8, letterSpacing: "0.1em", color: "rgba(255,255,255,0.32)", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color, fontFamily: "var(--font-mono)" }}>{val}</div>
    </div>
  );
  return (
    <div className="space-y-3">
      {/* Summary strip */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, padding: "12px 14px",
        background: "rgba(196,181,253,0.04)", border: "1px solid rgba(196,181,253,0.16)", borderRadius: 12 }}>
        {stat("Pairs", t.universe_size)}
        {stat("Strategies", t.pool_size, "#c4b5fd")}
        {stat("Books", t.books)}
        {stat("Ticks", t.ticks)}
        {stat("Trades", t.total_trades)}
        {stat("With edge", `${t.strategies_with_edge}/${t.strategies_traded}`, t.strategies_with_edge > 0 ? "#4dffaf" : "#e2e8f0")}
        {stat("Friction", `${t.fee_pct_per_side}%`, "#fbbf24")}
      </div>

      {/* Promotions */}
      {promotions && promotions.length > 0 && (
        <div style={{ padding: "10px 14px", background: "rgba(77,255,175,0.05)",
          border: "1px solid rgba(77,255,175,0.22)", borderRadius: 12 }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", color: "#4dffaf",
            textTransform: "uppercase", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <Trophy style={{ width: 12, height: 12 }} /> Graduated to forward-test ({promotions.length})
          </div>
          {promotions.slice(0, 6).map((p, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "3px 0", fontSize: 10 }}>
              <span style={{ color: "rgba(255,255,255,0.8)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
              <span style={{ color: "#4dffaf", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>
                {p.avg_net_return_pct > 0 ? "+" : ""}{p.avg_net_return_pct}% · {p.profitable_pairs} pairs · {p.trades}t
              </span>
            </div>
          ))}
        </div>
      )}

      {/* By-strategy edge board */}
      <div>
        <SectionLabel icon={Zap} label="Edge by Strategy" sub="net % after fees · ranked by idea, across all pairs" color="#c4b5fd" count={board.length} />
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
            <thead>
              <tr style={{ color: "rgba(255,255,255,0.4)", textAlign: "right" }}>
                <th style={{ textAlign: "left", padding: "5px 6px" }}>Strategy</th>
                <th style={{ padding: "5px 6px" }}>Net %</th>
                <th style={{ padding: "5px 6px" }}>Trades</th>
                <th style={{ padding: "5px 6px" }}>+Pairs</th>
                <th style={{ padding: "5px 6px" }}>WR</th>
              </tr>
            </thead>
            <tbody>
              {board.map((s, i) => (
                <tr key={s.id || i} onClick={() => onOpen && onOpen({ title: s.name, ...s })}
                  style={{ borderTop: "1px solid rgba(255,255,255,0.05)", cursor: "pointer",
                    background: promoted.has(s.id) ? "rgba(77,255,175,0.05)" : "transparent" }}>
                  <td style={{ padding: "5px 6px", maxWidth: 220 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <SourceBadge source={s.source} />
                      {/* `unnamed` = pre-registry book whose title was never
                          persisted; the label is derived from its id, so mark it
                          rather than let it read as a real strategy name. */}
                      <span
                        title={s.unnamed ? `Original title not recorded · id ${s.id}` : s.name}
                        style={{
                          color: s.unnamed ? "rgba(255,255,255,0.42)" : "rgba(255,255,255,0.85)",
                          fontStyle: s.unnamed ? "italic" : "normal",
                          fontFamily: s.unnamed ? "var(--font-mono)" : "inherit",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}
                      >{s.name}</span>
                      {promoted.has(s.id) && <Trophy style={{ width: 10, height: 10, color: "#4dffaf", flexShrink: 0 }} />}
                    </div>
                  </td>
                  <td style={{ padding: "5px 6px", textAlign: "right", fontFamily: "var(--font-mono)", fontWeight: 700, color: netColor(s.avg_net_return_pct) }}>
                    {s.avg_net_return_pct > 0 ? "+" : ""}{s.avg_net_return_pct}
                  </td>
                  <td style={{ padding: "5px 6px", textAlign: "right", color: "rgba(255,255,255,0.6)" }}>{s.trades}</td>
                  <td style={{ padding: "5px 6px", textAlign: "right", color: "rgba(255,255,255,0.6)" }}>{s.profitable_pairs}</td>
                  <td style={{ padding: "5px 6px", textAlign: "right", color: "rgba(255,255,255,0.6)" }}>{s.win_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Best pair × strategy combos */}
      {combos.length > 0 && (
        <div>
          <SectionLabel icon={BarChart3} label="Top Pair × Strategy Books" sub="best individual net-edge combos" color="#5cf2ff" count={combos.length} />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {combos.slice(0, 16).map((c, i) => (
              <div key={i} style={{ padding: "5px 9px", borderRadius: 8, fontSize: 9.5, fontFamily: "var(--font-mono)",
                background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <span style={{ color: "rgba(255,255,255,0.7)" }}>{c.symbol}</span>
                <span style={{ color: netColor(c.net_return_pct), fontWeight: 700, marginLeft: 6 }}>
                  {c.net_return_pct > 0 ? "+" : ""}{c.net_return_pct}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  UNIFIED RESEARCH LAB — pipeline · governance · regime · deployment · lessons
//  Part quant desk, part skunkworks, part command center. Every value below traces
//  to a real backtest or a real (paper) fill; unknowns render "insufficient data".
// ══════════════════════════════════════════════════════════════════════════════

const STAGE_META = {
  intake:     { label: "Idea Intake",  color: "#a78bfa", hint: "theories proposed" },
  testing:    { label: "In Testing",   color: "#5cf2ff", hint: "under backtest pressure" },
  live:       { label: "Live Monitor", color: "#fbbf24", hint: "forward-testing live" },
  promoted:   { label: "Promoted",     color: "#4dffaf", hint: "cleared the bar" },
  restricted: { label: "Restricted",   color: "#f87171", hint: "reverted / blocked" },
};

const GRADE_META = {
  approved:          { label: "Approved",      color: "#4dffaf" },
  monitor:           { label: "Monitor",       color: "#fbbf24" },
  restricted:        { label: "Restricted",    color: "#f87171" },
  insufficient_data: { label: "Insufficient",  color: "#94a3b8" },
};

function num(v, dp = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return typeof v === "number" ? v.toFixed(dp) : String(v);
}

// ── 1. Command-view top strip ─────────────────────────────────────────────────
function LabTopStrip({ status, counts, governance }) {
  const champ = status?.champion;
  const items = [
    { label: "Lab",           value: status?.running ? "RUNNING" : "IDLE", color: status?.running ? "#4dffaf" : "#94a3b8" },
    { label: "Regime",        value: (status?.regime || status?.adx != null) ? (status.regime || `ADX ${num(status.adx,1)}`) : "—", color: "#5cf2ff" },
    { label: "Pos. modifier", value: status?.position_multiplier != null ? `${num(status.position_multiplier)}×` : "—", color: "#a78bfa" },
    { label: "In testing",    value: counts?.testing ?? "—", color: "#5cf2ff" },
    { label: "Promoted",      value: counts?.promoted ?? "—", color: "#4dffaf" },
    { label: "Under review",  value: (governance?.buckets?.monitor ?? 0) + (governance?.buckets?.restricted ?? 0), color: "#fbbf24" },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(96px, 1fr))", gap: 6 }}>
      {items.map((it, i) => (
        <div key={i} style={{ padding: "8px 10px", background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 9 }}>
          <div style={{ fontSize: 8.5, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", fontFamily: "var(--font-mono)" }}>{it.label}</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: it.color, marginTop: 2 }}>{it.value}</div>
        </div>
      ))}
      {champ && (
        <div style={{ padding: "8px 10px", background: "rgba(77,255,175,0.06)", border: "1px solid rgba(77,255,175,0.2)", borderRadius: 9, gridColumn: "span 2" }}>
          <div style={{ fontSize: 8.5, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(77,255,175,0.6)", fontFamily: "var(--font-mono)" }}>Champion</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#4dffaf", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {champ.name} · SR {num(champ.sharpe_ratio)} · {num(champ.win_rate,1)}%
          </div>
        </div>
      )}
    </div>
  );
}

// ── 2 + 3. Pipeline board with candidate cards ───────────────────────────────
function CandidateCard({ c, onOpen }) {
  const stage = STAGE_META[c.stage] || STAGE_META.intake;
  return (
    <button onClick={() => onOpen(c)} style={{
      textAlign: "left", width: "100%", cursor: "pointer", marginBottom: 6,
      background: "rgba(255,255,255,0.025)", border: `1px solid ${stage.color}33`,
      borderRadius: 9, padding: "8px 9px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
        <div style={{ flex: 1, minWidth: 0, fontSize: 11, fontWeight: 700, color: "#e8eefc", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</div>
        {c.author && <span style={{ fontSize: 8.5, color: stage.color, fontWeight: 700, flexShrink: 0 }}>{c.author}</span>}
      </div>
      {c.thesis && <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.45)", marginTop: 3, lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{c.thesis}</div>}
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 5, alignItems: "center" }}>
        {c.market && c.market !== "—" && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 4, background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.5)" }}>{c.market}</span>}
        {typeof c.confidence === "number" && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 4, background: "rgba(251,191,36,0.1)", color: "#fbbf24" }}>conf {Math.round(c.confidence * 100)}%</span>}
        {c.sample_count != null && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 4, background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.5)" }}>{c.sample_count} trades</span>}
        {c.score != null && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 4, background: "rgba(92,242,255,0.1)", color: "#5cf2ff" }}>score {num(c.score,1)}</span>}
      </div>
      {c.last_result && <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", marginTop: 4, fontFamily: "var(--font-mono)" }}>{c.last_result}</div>}
    </button>
  );
}

function PipelineBoard({ pipeline, onOpen }) {
  const cols = pipeline?.columns || {};
  const order = ["intake", "testing", "live", "promoted", "restricted"];
  const [q, setQ] = useState("");
  const [src, setSrc] = useState(null);

  // ~94 candidates across five columns with no way to find one by name.
  const all = order.flatMap((s) => cols[s] || []);
  const sources = Array.from(new Set(all.map((c) => c.source).filter(Boolean))).sort();
  const needle = q.trim().toLowerCase();
  const match = (c) => {
    if (src && c.source !== src) return false;
    if (!needle) return true;
    return [c.name, c.author, c.thesis, c.market, c.id]
      .some((v) => typeof v === "string" && v.toLowerCase().includes(needle));
  };
  const shown = all.filter(match).length;

  return (
    <div>
      <SectionLabel icon={GitBranch} label="Candidate Pipeline" sub="Idea → pressure → truth" color="#a78bfa" />
      <FilterBar
        q={q} setQ={setQ} chips={sources} active={src} setActive={setSrc}
        placeholder="Find a candidate by name, author or thesis…"
        shown={shown} total={all.length}
      />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(168px, 1fr))", gap: 8, marginTop: 8 }}>
        {order.map(stage => {
          const meta = STAGE_META[stage];
          const full = cols[stage] || [];
          const list = full.filter(match);
          return (
            <div key={stage} style={{ background: "rgba(255,255,255,0.015)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 10, padding: 8, minHeight: 60 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 9.5, fontWeight: 700, color: meta.color, letterSpacing: "0.04em" }}>{meta.label}</span>
                <span style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", fontFamily: "var(--font-mono)" }}>
                  {list.length === full.length ? full.length : `${list.length}/${full.length}`}
                </span>
              </div>
              {list.length === 0
                ? <div style={{ fontSize: 9, color: "rgba(255,255,255,0.25)", fontStyle: "italic", padding: "4px 2px" }}>
                    {full.length === 0 ? "none" : "no match"}
                  </div>
                : list.map(c => <CandidateCard key={c.id} c={c} onOpen={onOpen} />)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── 5. Governance chamber ─────────────────────────────────────────────────────
function GovernanceChamber({ governance }) {
  const allItems = governance?.items || [];
  const bar = governance?.bar;
  const buckets = governance?.buckets || {};
  const [q, setQ] = useState("");
  const [grade, setGrade] = useState(null);
  const needle = q.trim().toLowerCase();
  const items = allItems.filter((it) => {
    if (grade && it.grade !== grade) return false;
    if (!needle) return true;
    return [it.name, it.author, it.reason, it.id]
      .some((v) => typeof v === "string" && v.toLowerCase().includes(needle));
  });
  const grades = Array.from(new Set(allItems.map((i) => i.grade).filter(Boolean)));
  return (
    <div>
      {/* No SectionLabel here: the enclosing <Section> already renders this
          title and subtitle, which rendered "Governance Chamber" twice. */}
      {bar && (
        <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", margin: "6px 0", fontFamily: "var(--font-mono)" }}>
          DEPLOY BAR · ≥{bar.min_trades} trades · net &gt; 0 after fees · ≥{bar.min_trades_per_pair} trades/pair
          · ≥{bar.min_forward_trades} forward trades · forward edge ≥{Math.round((bar.holdup_ratio ?? 0) * 100)}% of backtest
          · no duplicates{bar.dropped?.includes("min_win_rate") ? " · dropped: win rate" : ""}
        </div>
      )}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
        {Object.entries(GRADE_META).map(([k, m]) => (
          <span key={k} style={{ fontSize: 9, padding: "2px 8px", borderRadius: 6, background: `${m.color}1a`, color: m.color, fontWeight: 700 }}>
            {m.label} {buckets[k] || 0}
          </span>
        ))}
      </div>
      <FilterBar
        q={q} setQ={setQ} chips={grades} active={grade} setActive={setGrade}
        placeholder="Find a strategy by name, author or verdict…"
        shown={items.length} total={allItems.length}
      />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 6 }}>
        {items.slice(0, 24).map((it, i) => {
          const m = GRADE_META[it.grade] || GRADE_META.insufficient_data;
          return (
            <div key={i} style={{ padding: "8px 9px", background: "rgba(255,255,255,0.02)", border: `1px solid ${m.color}2a`, borderRadius: 9 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 11, fontWeight: 700, color: "#e8eefc", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.name}</span>
                <span style={{ fontSize: 8.5, fontWeight: 700, padding: "1px 7px", borderRadius: 5, background: `${m.color}1f`, color: m.color }}>{m.label}</span>
              </div>
              {(it.must_improve || []).length > 0 && (
                <div style={{ marginTop: 5 }}>
                  <div style={{ fontSize: 8, color: "rgba(255,255,255,0.35)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Must improve</div>
                  {it.must_improve.slice(0, 3).map((g, j) => (
                    <div key={j} style={{ fontSize: 9.5, color: "rgba(255,255,255,0.6)", fontFamily: "var(--font-mono)", marginTop: 2 }}>· {g}</div>
                  ))}
                </div>
              )}
              {it.promotes_when && <div style={{ fontSize: 9, color: "#4dffaf", marginTop: 4 }}>↑ {it.promotes_when}</div>}
            </div>
          );
        })}
        {items.length === 0 && <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>No measurable candidates yet.</div>}
      </div>
    </div>
  );
}

// ── 7. Regime intelligence matrix ─────────────────────────────────────────────
function RegimeMatrix({ matrix }) {
  const engines = matrix?.engines || [];
  const regimes = matrix?.regimes || [];
  const cells = matrix?.cells || {};
  const best = matrix?.best_regime || {};

  if (engines.length === 0 || regimes.length === 0) {
    return (
      <div>
        <SectionLabel icon={Activity} label="Regime Matrix" sub="Engine × regime edge" color="#5cf2ff" />
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 8, lineHeight: 1.5, padding: 10, background: "rgba(92,242,255,0.04)", border: "1px solid rgba(92,242,255,0.15)", borderRadius: 9 }}>
          Insufficient data yet. Regime-at-entry tagging is now live — this matrix fills as new trades close with a tagged regime. {matrix?.note}
        </div>
      </div>
    );
  }

  const expColor = v => v == null ? "rgba(255,255,255,0.2)" : v > 0 ? "#4dffaf" : v < 0 ? "#f87171" : "#94a3b8";
  return (
    <div>
      <SectionLabel icon={Activity} label="Regime Matrix" sub={`${matrix.days}d · expectancy $/trade`} color="#5cf2ff" />
      <div style={{ overflowX: "auto", marginTop: 8 }}>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 10 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "4px 8px", color: "rgba(255,255,255,0.4)", fontWeight: 600 }}>Engine</th>
              {regimes.map(r => <th key={r} style={{ padding: "4px 8px", color: "rgba(255,255,255,0.5)", fontWeight: 600, whiteSpace: "nowrap" }}>{r}</th>)}
            </tr>
          </thead>
          <tbody>
            {engines.map(eng => (
              <tr key={eng} style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                <td style={{ padding: "5px 8px", color: "#e8eefc", fontWeight: 600, whiteSpace: "nowrap" }}>
                  {eng}{best[eng] && <span style={{ color: "#4dffaf", fontSize: 8.5, marginLeft: 4 }}>★{best[eng]}</span>}
                </td>
                {regimes.map(r => {
                  const cell = cells[eng]?.[r];
                  if (!cell) return <td key={r} style={{ padding: "5px 8px", textAlign: "center", color: "rgba(255,255,255,0.15)" }}>·</td>;
                  if (!cell.sufficient) return (
                    <td key={r} style={{ padding: "5px 8px", textAlign: "center", color: "rgba(255,255,255,0.3)" }} title={`${cell.trades} trades — insufficient`}>
                      <span style={{ fontSize: 8 }}>n={cell.trades}</span>
                    </td>
                  );
                  return (
                    <td key={r} style={{ padding: "5px 8px", textAlign: "center" }} title={`${cell.trades} trades · WR ${Math.round((cell.win_rate||0)*100)}%`}>
                      <div style={{ color: expColor(cell.expectancy_pnl), fontWeight: 700, fontFamily: "var(--font-mono)" }}>{cell.expectancy_pnl > 0 ? "+" : ""}{num(cell.expectancy_pnl)}</div>
                      <div style={{ fontSize: 7.5, color: "rgba(255,255,255,0.3)" }}>{Math.round((cell.win_rate||0)*100)}% · {cell.trades}t</div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 8.5, color: "rgba(255,255,255,0.3)", marginTop: 6 }}>★ = best regime · cells below {matrix.min_cell_trades} trades shown as n=… (insufficient)</div>
    </div>
  );
}

// ── 8. Deployment board (production bridge) ───────────────────────────────────
function DeploymentBoard({ board }) {
  const deps = board?.deployments || [];
  return (
    <div>
      <SectionLabel icon={Zap} label="Deployment Board" sub="Production bridge · real-money reality" color="#fbbf24" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 6, marginTop: 8 }}>
        {deps.map((d, i) => {
          const drift = d.drift;
          const driftColor = drift ? (drift.direction === "improving" ? "#4dffaf" : "#f87171") : "rgba(255,255,255,0.3)";
          return (
            <div key={i} style={{ padding: "9px 10px", background: "rgba(251,191,36,0.04)", border: "1px solid rgba(251,191,36,0.18)", borderRadius: 9 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 11, fontWeight: 700, color: "#e8eefc", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.name}</span>
                <span style={{ fontSize: 8, fontWeight: 700, padding: "1px 6px", borderRadius: 5, background: "rgba(251,191,36,0.15)", color: "#fbbf24" }}>{d.trust_tier}</span>
              </div>
              {d.earned_because && <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", marginTop: 4, lineHeight: 1.45 }}>{d.earned_because}</div>}
              {d.live?.net_return_pct != null && <div style={{ fontSize: 9.5, marginTop: 4, color: d.live.net_return_pct >= 0 ? "#4dffaf" : "#f87171", fontFamily: "var(--font-mono)" }}>net {d.live.net_return_pct > 0 ? "+" : ""}{num(d.live.net_return_pct)}% · {d.live.trades}t · {d.live.profitable_pairs} pairs</div>}
              {drift && <div style={{ fontSize: 9.5, marginTop: 4, color: driftColor, fontFamily: "var(--font-mono)" }}>drift {drift.live_return_pct > 0 ? "+" : ""}{num(drift.live_return_pct)}% · {drift.direction}</div>}
            </div>
          );
        })}
        {deps.length === 0 && <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>Nothing deployed to production yet.</div>}
      </div>
    </div>
  );
}

// ── 6. Lessons vault ──────────────────────────────────────────────────────────
function LessonsVault({ lessons }) {
  const all = lessons?.lessons || [];
  const labels = lessons?.category_labels || {};
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(null);
  const needle = q.trim().toLowerCase();
  const list = all.filter((l) => {
    if (cat && l.category !== cat) return false;
    if (!needle) return true;
    return [l.name, l.reason, l.category]
      .some((v) => typeof v === "string" && v.toLowerCase().includes(needle));
  });
  const cats = Array.from(new Set(all.map((l) => l.category).filter(Boolean)));
  const byCat = lessons?.by_category || {};
  const catColor = { execution: "#f87171", thin_sample: "#94a3b8", overfit: "#fbbf24", regime_mismatch: "#a78bfa", rejected: "#fb923c" };
  return (
    <div>
      <SectionLabel icon={AlertTriangle} label="Lessons Vault" sub="What didn't work — so we learn" color="#fb923c" />
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "6px 0 8px" }}>
        {Object.entries(byCat).map(([k, n]) => (
          <span key={k} style={{ fontSize: 9, padding: "2px 8px", borderRadius: 6, background: `${catColor[k] || "#94a3b8"}1a`, color: catColor[k] || "#94a3b8", fontWeight: 700 }}>{labels[k] || k} {n}</span>
        ))}
      </div>
      <FilterBar
        q={q} setQ={setQ} chips={cats} active={cat} setActive={setCat}
        placeholder="Find a lesson by name or reason…"
        shown={list.length} total={all.length}
      />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 6 }}>
        {list.slice(0, 30).map((l, i) => {
          const col = catColor[l.category] || "#94a3b8";
          return (
            <div key={i} style={{ padding: "8px 9px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 9 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 10.5, fontWeight: 700, color: "#e8eefc", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.name || "untitled"}</span>
                <span style={{ fontSize: 8, fontWeight: 700, padding: "1px 6px", borderRadius: 5, background: `${col}1f`, color: col }}>{labels[l.category] || l.category}</span>
              </div>
              {l.reason && <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", marginTop: 3, lineHeight: 1.4 }}>{l.reason}</div>}
              {l.metrics && (l.metrics.sharpe_ratio != null || l.metrics.total_trades != null) && (
                <div style={{ fontSize: 8.5, color: "rgba(255,255,255,0.35)", marginTop: 3, fontFamily: "var(--font-mono)" }}>
                  {l.metrics.sharpe_ratio != null && `SR ${num(l.metrics.sharpe_ratio)} · `}{l.metrics.win_rate != null && `${num(l.metrics.win_rate,1)}% · `}{l.metrics.total_trades != null && `${l.metrics.total_trades}t`}
                </div>
              )}
            </div>
          );
        })}
        {list.length === 0 && <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>No failed experiments recorded yet.</div>}
      </div>
    </div>
  );
}

// ── Council ⇄ Lab loop strip ──────────────────────────────────────────────────
function CouncilLoopStrip({ requests }) {
  if (!requests || requests.length === 0) return null;
  const sColor = { open: "#94a3b8", researching: "#5cf2ff", answered: "#4dffaf" };
  return (
    <div style={{ padding: "9px 11px", background: "rgba(167,139,250,0.05)", border: "1px solid rgba(167,139,250,0.2)", borderRadius: 10 }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, color: "#a78bfa", marginBottom: 5 }}>🔁 Council → Lab · working the council's open conflicts</div>
      {requests.slice(0, 4).map((r, i) => (
        <div key={i} style={{ display: "flex", gap: 7, alignItems: "flex-start", marginTop: 4 }}>
          <span style={{ fontSize: 8, fontWeight: 700, padding: "1px 6px", borderRadius: 5, background: `${sColor[r.status] || "#94a3b8"}1f`, color: sColor[r.status] || "#94a3b8", flexShrink: 0, marginTop: 1 }}>{r.status}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.7)" }}>{r.question}</div>
            {r.verdict && <div style={{ fontSize: 9, color: "#4dffaf", marginTop: 1 }}>→ {r.verdict}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function AgentFeedPanel() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const d = await fetch(`${API}/researcher/feed?limit=40`).then(r => r.json());
      setEvents(d.events || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 20000); return () => clearInterval(id); }, [load]);

  const KIND_COLOR = { council: "#7dd3fc", thread: "#c4b5fd", finding: "#34d399", action: "#fbbf24", promotion: "#f472b6" };
  const shortTs = (ts) => { if (!ts || !ts.includes("T")) return ts || ""; const [d, t] = ts.split("T"); return `${d.slice(5)} ${t.slice(0, 5)}`; };

  return (
    <div className="rounded-2xl overflow-hidden"
      style={{ background: "linear-gradient(135deg, rgba(125,211,252,0.05), rgba(255,255,255,0.02))", border: "1px solid rgba(255,255,255,0.08)" }}>
      <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <span style={{ fontSize: 18 }}>🗞️</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-white">Live Agent Feed</div>
          <div className="text-[9px]" style={{ color: "rgba(255,255,255,0.35)" }}>
            Everything the research org is doing — verdicts, findings, strategies, ACT changes, theory threads
          </div>
        </div>
        <span className="text-[9px] px-2 py-1 rounded-full" style={{ background: "rgba(52,211,153,0.15)", color: "#34d399" }}>● live</span>
      </div>
      {loading ? (
        <div className="px-4 py-6 text-center text-[11px]" style={{ color: "rgba(255,255,255,0.3)" }}>Loading…</div>
      ) : events.length === 0 ? (
        <div className="px-4 py-6 text-center text-[11px]" style={{ color: "rgba(255,255,255,0.4)" }}>No agent activity yet.</div>
      ) : (
        <div style={{ maxHeight: 480, overflowY: "auto" }}>
          {events.map((e, i) => (
            <div key={i} className="px-4 py-2.5" style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
              <div className="flex items-center gap-2">
                <span style={{ fontSize: 13 }}>{e.icon}</span>
                <span className="text-[11px] font-bold" style={{ color: KIND_COLOR[e.kind] || "#fff" }}>{e.actor}</span>
                <span className="text-[11px] text-white flex-1 min-w-0 truncate">{e.title}</span>
                <span className="text-[9px]" style={{ color: "rgba(255,255,255,0.3)" }}>{shortTs(e.ts)}</span>
              </div>
              {e.detail && <div className="text-[10px] mt-1 ml-6" style={{ color: "rgba(255,255,255,0.45)" }}>{e.detail}</div>}
              {e.status && <div className="text-[9px] mt-1 ml-6 font-medium" style={{ color: KIND_COLOR[e.kind] || "rgba(255,255,255,0.5)" }}>{e.status}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Redesigned shell (2026-07-27) ─────────────────────────────────────────────
// Navigation groups the org by what you're asking, not by which service wrote
// the data: Overview answers "is it healthy + what's it found", Pipeline is the
// idea→promotion funnel, Discovery is where edge is invented, Desk is the
// autonomous org, Signals is live intel, Library is source material, System is
// infrastructure.
const TABS = [
  { id: "overview",  label: "Overview",  icon: FlaskConical, hint: "Health + headline numbers" },
  { id: "pipeline",  label: "Pipeline",  icon: GitBranch,    hint: "Idea → backtest → promoted" },
  { id: "discovery", label: "Discovery", icon: Cpu,          hint: "Where edge is invented" },
  { id: "desk",      label: "Desk",      icon: Atom,         hint: "The autonomous quant org" },
  { id: "signals",   label: "Signals",   icon: Activity,     hint: "Live market intelligence" },
  { id: "library",   label: "Library",   icon: BookOpen,     hint: "Papers + ingested research" },
  { id: "system",    label: "System",    icon: Shield,       hint: "NEXUS + self-healing" },
];

// Returns null rather than a clamped value when the timestamp is unusable, so
// the caller can omit the label entirely instead of claiming "just now".
const _agoLabel = (iso) => {
  if (!iso) return null;
  const t = parseApiTime(iso);
  if (!Number.isFinite(t)) return null;
  return ago(iso);
};

/** Live status pill — dot + label + value, colour carries the state. */
function CmdPill({ label, value, tone = "#45c9e8", title }) {
  return (
    <div className="rlab-cmd-pill" title={title || label}>
      <span className="rlab-cmd-dot" style={{ background: tone, boxShadow: `0 0 6px ${tone}66` }} />
      <span className="rlab-cmd-pill-lbl">{label}</span>
      <span className="rlab-cmd-pill-val" style={{ color: tone }}>{value}</span>
    </div>
  );
}

/** Small infrastructure dot — alive/dead only, no number worth a whole pill. */
function CmdDot({ label, on, tone = "#4dffaf", title }) {
  return (
    <span className="rlab-cmd-mini" title={title || label}>
      <span className="rlab-cmd-dot" style={{
        background: on ? tone : "rgba(255,255,255,0.22)",
        boxShadow: on ? `0 0 6px ${tone}66` : "none",
      }} />
      <span className="rlab-cmd-mini-lbl" style={{ color: on ? undefined : "rgba(255,255,255,0.32)" }}>{label}</span>
    </span>
  );
}

/**
 * Command header — identity left, live vitals right.
 *
 * This used to be two stacked bands: CommandHeader (LAB/DESK/TOURNEY/PULSE/LINKS)
 * and a separate VitalsStrip (NEXUS/OMEGA/ORIA/TESTING/PROMOTED). TESTING and
 * PROMOTED simply repeated the Overview KPI cards a row further down, and the
 * three infrastructure flags are alive/dead booleans that never needed a pill
 * each. Folded into one rail: pills carry numbers, dots carry liveness.
 *
 * 2026-08-25 redesign: the tab row and the tab-purpose line were two MORE
 * bands below this one — ~140px of chrome before any content, and all three
 * scrolled away exactly when you needed to know which tab you were on. They
 * are now rows of this component, and the whole thing is sticky.
 */
function CommandHeader({ lab, desk, tournament, omega, nexus, updatedAt, connections, onRefresh, refreshing, tab, onTab }) {
  const labOk   = lab?.running;
  const deskOk  = desk?.enabled && desk?.running;
  const hbAge   = lab?.health?.tournament_hb_age_s;
  const hbOk    = typeof hbAge === "number" ? hbAge < 300 : null;
  const nexusLive = nexus?.live;
  return (
    <div className="rlab-cmd">
      <div className="rlab-cmd-top">
      <div className="rlab-cmd-id">
        <FlaskConical className="rlab-cmd-icon" />
        <div style={{ minWidth: 0 }}>
          <div className="rlab-cmd-title">Research Lab</div>
          <div className="rlab-cmd-sub">Where AEON invents, tests and promotes its own trading edge</div>
        </div>
      </div>
      <div className="rlab-cmd-pills">
        <CmdPill
          label="LAB" tone={labOk ? "#d9ff57" : "#ff5d6c"}
          value={labOk ? `gen ${num(lab?.generation, 0)}` : "stopped"}
          title={`Lab cycles: ${num(lab?.cycles, 0)} · evaluated ${num(lab?.total_evaluated, 0)}`}
        />
        <CmdPill
          label="DESK" tone={deskOk ? "#4dffaf" : "#647083"}
          value={deskOk ? `${num(desk?.cycles, 0)} cyc` : "paused"}
          title={`Proposals ${num(desk?.total_proposals, 0)} → survived ${num(desk?.total_survived, 0)}`}
        />
        <CmdPill
          label="TOURNEY" tone="#c4b5fd"
          value={`${num(tournament?.ticks, 0)} ticks`}
          title={`${num(tournament?.total_trades, 0)} sim trades across ${num(tournament?.universe_size, 0)} pairs`}
        />
        <CmdPill
          label="PULSE" tone={hbOk === null ? "#647083" : hbOk ? "#4dffaf" : "#f5b042"}
          value={typeof hbAge === "number" ? `${Math.round(hbAge)}s` : "—"}
          title="Tournament heartbeat age"
        />
        <CmdPill
          label="LINKS"
          tone={connections?.failed ? "#f5b042" : "#4dffaf"}
          value={connections ? `${connections.ok}/${connections.total}` : "—"}
          title={connections?.failedNames?.length
            ? `Unavailable: ${connections.failedNames.join(", ")}`
            : "All research data connections responded"}
        />
        <span className="rlab-cmd-dots">
          <CmdDot label="NEXUS" on={nexusLive} tone="#4dffaf"
            title={nexusLive ? `NEXUS live · regime ${nexus?.config?.regime || "—"}` : "NEXUS offline"} />
          <CmdDot label="OMEGA" on={omega?.governance_writes} tone="#c4b5fd"
            title={omega?.governance_writes ? `OMEGA cycle ${omega?.cycle_count ?? 0}` : "OMEGA warming up"} />
          <CmdDot label="ORIA" on={omega?.oria_connected} tone="#5cf2ff"
            title={omega?.oria_connected ? "ORIA wired" : "ORIA not connected"} />
        </span>
        {updatedAt && <span className="rlab-cmd-updated">{updatedAt}</span>}
        <button
          type="button"
          className="rlab-refresh"
          onClick={onRefresh}
          disabled={refreshing}
          title="Refresh every research connection now"
          aria-label="Refresh research data"
        >
          <RefreshCw style={{ width: 11, height: 11, animation: refreshing ? "spin 1s linear infinite" : "none" }} />
          {refreshing ? "SYNCING" : "REFRESH"}
        </button>
      </div>
      </div>

      {/* Row 2 — navigation */}
      <div className="rlab-tabs" role="tablist">
        {TABS.map(({ id, label, icon: Icon, hint }) => (
          <button
            key={id}
            role="tab"
            aria-selected={tab === id}
            title={hint}
            onClick={() => onTab(id)}
            className={`rlab-tab ${tab === id ? "is-active" : ""}`}
          >
            <Icon style={{ width: 12, height: 12 }} />
            {label}
          </button>
        ))}
      </div>

      {/* Row 3 — what this tab is for, so the screen explains itself */}
      <div className="rlab-tabhint">{TABS.find((x) => x.id === tab)?.hint}</div>
    </div>
  );
}

function ResearchFlow({ data, onGo }) {
  const cols = data.pipeline?.columns || {};
  const steps = [
    {
      id: "library", label: "INGEST", value: data.research?.length || 0,
      sub: "papers + market feeds", tone: "#93c5fd",
    },
    {
      id: "discovery", label: "INVENT", value: num(data.lab?.total_evaluated, 0),
      sub: "ideas evaluated", tone: "#5cf2ff",
    },
    {
      id: "discovery", label: "PROVE", value: num(data.tournament?.total_trades, 0),
      sub: "after-fee sim trades", tone: "#c4b5fd",
    },
    {
      id: "pipeline", label: "GOVERN", value: (cols.testing || []).length,
      sub: "under live scrutiny", tone: "#fbbf24",
    },
    {
      id: "pipeline", label: "PROMOTE", value: (cols.promoted || []).length,
      sub: "earned deployment", tone: "#4dffaf",
    },
  ];
  return (
    <div className="rlab-flow" aria-label="Research lifecycle">
      <div className="rlab-flow-title">
        <GitBranch style={{ width: 12, height: 12 }} />
        HOW RESEARCH BECOMES A LIVE EDGE
      </div>
      <div className="rlab-flow-track">
        {steps.map((step, i) => (
          <React.Fragment key={`${step.label}-${i}`}>
            <button className="rlab-flow-step" type="button" onClick={() => onGo(step.id)}>
              <span className="rlab-flow-label" style={{ color: step.tone }}>{step.label}</span>
              <strong>{step.value}</strong>
              <small>{step.sub}</small>
            </button>
            {i < steps.length - 1 && <span className="rlab-flow-arrow">→</span>}
          </React.Fragment>
        ))}
      </div>
      {data.connections?.failed > 0 && (
        <div className="rlab-connection-warning">
          <AlertTriangle style={{ width: 11, height: 11 }} />
          {data.connections.failed} connection{data.connections.failed === 1 ? "" : "s"} unavailable:
          {" "}{data.connections.failedNames.join(", ")}. Empty panels may reflect a feed outage, not zero activity.
        </div>
      )}
    </div>
  );
}

/** One headline metric in the overview deck. */
function KpiCard({ label, value, sub, tone = "#e2e8f0", onClick, accent, hint }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      className={`rlab-kpi${onClick ? " is-clickable" : ""}`}
      onClick={onClick}
      title={hint}
      style={accent ? { borderLeft: `2px solid ${accent}` } : undefined}
    >
      <div className="rlab-kpi-lbl">{label}</div>
      <div className="rlab-kpi-val" style={{ color: tone }}>{value}</div>
      {sub && <div className="rlab-kpi-sub">{sub}</div>}
    </Tag>
  );
}

/**
 * Overview deck — the "what do I need to know" screen. Previously the Overview
 * tab held the whole pipeline funnel, which buried the headline numbers; the
 * funnel now lives in its own Pipeline tab and this answers health + findings.
 */
function OverviewDeck({ data, onGo }) {
  const t     = data.tournament;
  const desk  = data.desk;
  const lab   = data.lab;
  const cols  = data.pipeline?.columns || {};
  const promoted = (cols.promoted || []).length;
  const testing  = (cols.testing || []).length;
  const live     = (cols.live || []).length;
  const deployed       = (data.deployBoard?.deployments || []).length;
  const tourneyPromoted = (t?.promoted || []).length;
  const promoLedger    = (data.promotions || []).length;

  // Per-source table: which KIND of idea actually earns money after fees.
  const bySource = (t?.by_source || []).slice().sort(
    (a, b) => (b.avg_net_return_pct ?? -99) - (a.avg_net_return_pct ?? -99));

  // Freshness of every ingest feed, derived from each panel's fetched_at.
  // Report the OLDEST source in each group, not the newest: a group is only as
  // fresh as its stalest member, and taking the min let one live source hide a
  // dead one (dune_stablecoin_supply_eth sat 43 days stale behind a green cell).
  const feedAge = (src) => {
    const entries = Object.entries(src || {})
      .filter(([, v]) => v?.fetched_at)
      // parseApiTime, not new Date: an offset-less fetched_at read as local
      // time put every feed 5 hours in the future ("OKX -297m").
      .map(([name, v]) => ({ name, h: Math.max(0, (Date.now() - parseApiTime(v.fetched_at)) / 3600000) }))
      .filter((e) => Number.isFinite(e.h));
    if (!entries.length) return { h: null, worst: null, n: 0 };
    const worst = entries.reduce((a, b) => (b.h > a.h ? b : a));
    return { h: worst.h, worst: worst.name, n: entries.length };
  };
  const feeds = [
    { k: "OKX",     ...feedAge(data.okxIntel) },
    { k: "WEEX",    ...feedAge(data.weexIntel) },
    { k: "ONCHAIN", ...feedAge(data.onchain) },
    { k: "MACRO",   ...feedAge(data.macro) },
  ];

  // ── digest — plain-language "what's going on right now" ──────────────────
  const nexus = data.nexus || {};
  const regime = nexus.market_regime || nexus.regime;
  const haltActive = !!nexus.crisis || regime === "CHAOTIC";
  const H_market = typeof nexus.H_market === "number" ? nexus.H_market : null;
  const health_H = typeof nexus.health_H === "number" ? nexus.health_H : null;

  const newestPromo = (data.promotions || [])
    .slice()
    .sort((a, b) => new Date(b.promoted_at || 0) - new Date(a.promoted_at || 0))[0];
  const newestDisco = (data.labDiscoveries || [])[0];

  return (
    <div className="space-y-3">
      <ResearchFlow data={data} onGo={onGo} />

      <div className={`rlab-banner ${haltActive ? "tone-warn" : "tone-good"}`}>
        <span className="rlab-banner-dot" />
        <div className="rlab-banner-text">
          {haltActive ? (
            <><strong>Execution is paused at the NEXUS regime gate.</strong> The lab keeps
              working underneath — halting stops trades, not research.{" "}
              {num(t?.strategies_traded, 0)} strategies have been traded to date and{" "}
              {num(t?.strategies_with_edge, 0)} currently carry a measurable edge.</>
          ) : (
            <><strong>NEXUS regime: {regime || "unknown"}.</strong> Engines are trading normally.{" "}
              {num(t?.strategies_with_edge, 0)} of {num(t?.strategies_traded, 0)} traded strategies
              currently carry a measurable edge.</>
          )}
        </div>
        <a href="#" className="rlab-banner-link" onClick={(e) => { e.preventDefault(); onGo("system"); }}>Gate trace →</a>
      </div>

      <div className="rlab-digest">
        <div className="rlab-digest-feed">
          <div className={`rlab-stmt ${haltActive ? "tone-accent" : "tone-accent2"}`}>
            <span className="rlab-stmt-ic">
              {haltActive
                ? <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M15 9l-6 6"/><path d="M9 9l6 6"/></svg>
                : <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>}
            </span>
            <div className="rlab-stmt-body">
              <div className="rlab-stmt-h">
                {haltActive
                  ? "No engine can place a trade right now — the market itself tripped the breaker."
                  : "Engines are clear to trade — no regime breaker is active."}
              </div>
              <p className="rlab-stmt-p">
                {H_market !== null
                  ? <>Cross-asset entropy (H_market) is {H_market.toFixed(3)}
                      {regime ? ` — a ${regime} regime` : ""}.</>
                  : "No live entropy reading available."}
                {health_H !== null ? <> System health H is {health_H.toFixed(3)}.</> : ""}
                {" "}{haltActive
                  ? "NEXUS suspends engines rather than size trades down once a regime this noisy has no measurable edge to size for."
                  : "NEXUS scales position size with regime confidence rather than a hard on/off switch."}
              </p>
            </div>
            <a href="#" className="rlab-stmt-link" onClick={(e) => { e.preventDefault(); onGo("system"); }}>Why →</a>
          </div>

          {newestPromo ? (
            <div className="rlab-stmt tone-accent2">
              <span className="rlab-stmt-ic"><svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span>
              <div className="rlab-stmt-body">
                <div className="rlab-stmt-h">A new strategy was promoted{_agoLabel(newestPromo.promoted_at) ? `, ${_agoLabel(newestPromo.promoted_at)}` : ""}.</div>
                <p className="rlab-stmt-p" title={newestPromo.name}>
                  "{newestPromo.name}"{newestPromo.strategy?.author ? ` (author: ${newestPromo.strategy.author})` : ""} cleared
                  promotion — {typeof newestPromo.win_rate === "number" ? `${newestPromo.win_rate.toFixed(1)}% win rate` : "win rate n/a"} over{" "}
                  {num(newestPromo.trades, 0)} trades{typeof newestPromo.oos_net_pct === "number" ? `, ${newestPromo.oos_net_pct >= 0 ? "+" : ""}${newestPromo.oos_net_pct.toFixed(2)}% OOS net` : ""}
                  {typeof newestPromo.realized_pnl === "number" ? `, $${newestPromo.realized_pnl.toFixed(0)} realized so far` : ""}.
                </p>
              </div>
              <a href="#" className="rlab-stmt-link" onClick={(e) => { e.preventDefault(); onGo("discovery"); }}>Open →</a>
            </div>
          ) : (
            <div className="rlab-stmt">
              <span className="rlab-stmt-ic"><svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2v7.5a2 2 0 0 1-.21.9L4.72 20.55a1 1 0 0 0 .9 1.45h12.76a1 1 0 0 0 .9-1.45L14.21 10.4a2 2 0 0 1-.21-.9V2"/><path d="M8.5 2h7"/><path d="M7 16h10"/></svg></span>
              <div className="rlab-stmt-body">
                <div className="rlab-stmt-h">Nothing new has been promoted yet.</div>
                <p className="rlab-stmt-p">{promoted} theories currently sit at the "promoted" pipeline stage, {testing} more are in test.</p>
              </div>
              <a href="#" className="rlab-stmt-link" onClick={(e) => { e.preventDefault(); onGo("discovery"); }}>Board →</a>
            </div>
          )}

          {newestDisco && (
            <div className="rlab-stmt">
              <span className="rlab-stmt-ic"><svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg></span>
              <div className="rlab-stmt-body">
                <div className="rlab-stmt-h">Most recent lab event: {newestDisco.type || "update"}.</div>
                <p className="rlab-stmt-p" title={newestDisco.name}>{newestDisco.name}{_agoLabel(newestDisco.at) ? ` — ${_agoLabel(newestDisco.at)}` : ""}.</p>
              </div>
              <a href="#" className="rlab-stmt-link" onClick={(e) => { e.preventDefault(); onGo("discovery"); }}>Discoveries →</a>
            </div>
          )}

          <div className="rlab-tagrow">
            <span className="rlab-tagrow-lbl">Underneath all that:</span>
            <span className="rlab-tag">{num(data.lab?.total_authored, 0)} strategies authored</span>
            <span className="rlab-tag">{num(data.lab?.total_evolved, 0)} evolved</span>
            <span className="rlab-tag">{num(t?.total_trades, 0)} sim trades run</span>
            <span className="rlab-tag">{num((data.promotions || []).length, 0)} promoted</span>
          </div>
        </div>

        <div className="rlab-digest-rail">
          <div>
            <div className="rlab-kicker">NEXUS regime</div>
            <div className="rlab-gauge-row">
              <div className="rlab-gauge" style={{ background: haltActive ? "rgba(255,93,108,0.16)" : "rgba(77,255,175,0.16)" }}>
                <div className="rlab-gauge-val" style={{ color: haltActive ? "#ff5d6c" : "#4dffaf" }}>
                  {H_market !== null ? H_market.toFixed(3) : "—"}
                </div>
                <div className="rlab-gauge-sub">H_market{regime ? ` · ${regime}` : ""}</div>
              </div>
              <div className="rlab-gauge-note">
                {haltActive
                  ? "Engines paused. Clears when entropy drops back under the regime-change threshold — not a manual setting."
                  : "No breaker active — engines size normally against current regime confidence."}
              </div>
            </div>
          </div>
          <div>
            <div className="rlab-kicker">Lab pipeline</div>
            <p className="rlab-foot-note">
              Generation {num(data.lab?.generation, 0)}, cycle {num(data.lab?.cycles, 0)}
              {data.lab?.running ? ", running continuously" : ", currently stopped"}.{" "}
              {num(t?.pool_size, 0)} strategies in the active pool out of {num(t?.strategies_traded, 0)} traded since inception.
            </p>
          </div>
        </div>
      </div>

      <div className="rlab-section-lbl">Supporting detail — expand if you want it</div>
      <div className="rlab-kpi-grid">
        <KpiCard
          label="Strategies with edge" accent="#d9ff57"
          value={num(t?.strategies_with_edge, 0)}
          sub={`of ${num(t?.strategies_traded, 0)} traded · pool ${num(t?.pool_size, 0)}`}
          tone="#d9ff57" onClick={() => onGo("discovery")}
        />
        <KpiCard
          label="Sim trades (after fees)" accent="#c4b5fd"
          value={num(t?.total_trades, 0)}
          sub={`${num(t?.universe_size, 0)} pairs · ${t?.timeframe || "—"} bars`}
          onClick={() => onGo("discovery")}
        />
        {/* "Promoted" appeared four times on this page with four values from four
            sources (pipeline column 41, tournament ids 86, promotions ledger 50,
            deployment board 29) and nothing said they meant different things.
            The deployment board is the only one that means "reached a book", so
            that's the headline; the rest are named for the gate they cleared. */}
        <KpiCard
          label="Deployed" accent="#4dffaf"
          value={deployed} tone="#4dffaf"
          sub={`${promoted} cleared pipeline · ${testing} in test · ${live} live`}
          onClick={() => onGo("pipeline")}
          hint={`Deployed = strategies that actually reached a book (Deployment Board).
Cleared pipeline = ${promoted} theories at the "promoted" stage.
Cleared tournament bar = ${tourneyPromoted} strategy ids beating the promo bar.
Forward-test ledger = ${promoLedger} rows.
These count different gates — they are not meant to agree.`}
        />
        <KpiCard
          label="Desk theories" accent="#7dd3fc"
          value={num(desk?.total_proposals, 0)}
          sub={`${num(desk?.total_survived, 0)} survived scrutiny`}
          onClick={() => onGo("desk")}
        />
      </div>

      <div className="rlab-cols">
        <Section title="Edge by idea type" sub="Which family of idea pays, net of fees" icon={Trophy} color="#d9ff57" defaultOpen>
          {bySource.length === 0 ? (
            <Empty kind="waiting">No tournament results yet — books need ~10 min of live ticks before they trade.</Empty>
          ) : (
            <div className="rlab-srctable">
              {bySource.map((s) => {
                const net = s.avg_net_return_pct;
                const tone = netColor(net);
                return (
                  <div key={s.source} className="rlab-srcrow">
                    <SourceBadge source={s.source} />
                    <div className="rlab-srcrow-best" title={s.best?.name || ""}>
                      {s.best?.name || "—"}
                    </div>
                    <div className="rlab-srcrow-win">{s.winners}/{s.traded} win</div>
                    <div className="rlab-srcrow-net" style={{ color: tone }}>
                      {typeof net === "number" ? `${net > 0 ? "+" : ""}${net.toFixed(2)}%` : "—"}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        <Section title="Research feeds" sub="Ingest freshness · hourly timers" icon={Activity} color="#45c9e8" defaultOpen>
          <div className="rlab-feedgrid">
            {feeds.map((f) => {
              const dead  = f.h !== null && f.h > 24;
              const stale = f.h === null || f.h > 3;
              const tone  = dead ? "#ff5d6c" : stale ? "#f5b042" : "#4dffaf";
              const age = f.h === null ? "no data"
                : f.h < 1 ? `${Math.round(f.h * 60)}m`
                : f.h < 48 ? `${f.h.toFixed(1)}h`
                : `${Math.round(f.h / 24)}d`;
              return (
                <div
                  key={f.k}
                  className="rlab-feedcell"
                  title={f.worst
                    ? `Oldest of ${f.n} source${f.n === 1 ? "" : "s"}: ${f.worst} (${age})`
                    : "No source reported a fetch time"}
                >
                  <span className="rlab-feed-dot" style={{ background: tone }} />
                  <span className="rlab-feed-k">{f.k}</span>
                  <span className="rlab-feed-v" style={{ color: stale ? tone : "#8a93a3" }}>{age}</span>
                  {stale && f.worst && (
                    <span className="rlab-feed-worst" style={{ color: tone }}>{f.worst}</span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="rlab-feed-note">
            {data.research?.length || 0} papers ingested · {data.labDiscoveries?.length || 0} recent discoveries
          </div>
        </Section>
      </div>

      <Section title="Latest discoveries" sub="What the lab surfaced most recently" icon={Zap} color="#fbbf24" defaultOpen>
        {(data.labDiscoveries || []).length === 0 ? (
          <Empty kind="zero" detail="Behavioural-hash dedup suppresses repeat announcements of the same finding.">Quiet — dedup guards are suppressing repeats, not hiding failures.</Empty>
        ) : (
          <div className="rlab-disc-list">
            {(data.labDiscoveries || []).slice(0, 6).map((d, i) => (
              <div key={i} className="rlab-disc-row">
                <span className="rlab-disc-type">{d.type || "event"}</span>
                <span className="rlab-disc-name" title={d.name}>{d.name}</span>
                <span className="rlab-disc-when">{_agoLabel(d.at) || ""}</span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

const RESEARCH_BATCH_SIZE = 6;
const RESEARCH_BATCH_GAP_MS = 700;
const RESEARCH_INITIAL_DELAY_MS = 850;
const RESEARCH_FAST_MS = 20000;    // header vitals — things that actually move
const RESEARCH_SLOW_MS = 300000;   // everything else — lab cycles 20min, desk 60min

/**
 * Every research connection, declared once.
 *
 * `speed` — "fast" connections back the header pills and refresh on a 20s beat.
 *   Everything else is "slow": the lab cycles every 1200s, the desk every 3600s
 *   and the ingest timers hourly, so polling them at 45s was ~470KB/refresh of
 *   almost entirely unchanged data.
 * `tabs` — null means the connection is needed no matter what's on screen
 *   (header, or the Overview deck). Otherwise it's only fetched while one of the
 *   listed tabs is open, so opening System no longer pulls the Library.
 * `pick` — maps the response body into the flat `data` shape the panels expect.
 */
const CONNECTIONS = [
  // ── always-on: header pills + overview deck ────────────────────────────────
  { key: "lab",        name: "lab",               speed: "fast", tabs: null,
    url: "/researcher/lab/status",             pick: (d) => ({ lab: d }) },
  { key: "desk",       name: "desk",              speed: "fast", tabs: null,
    url: "/researcher/desk/status",            pick: (d) => ({ desk: d }) },
  // Slow despite backing a header pill: it's 26KB and `ticks` is a monotonic
  // counter, so a 20s beat spent half the fast budget to move a number nobody
  // watches second-to-second. Liveness is carried by lab/desk/nexus above.
  { key: "tournament", name: "tournament",        speed: "slow", tabs: null,
    url: "/researcher/quantum/tournament?top=40", pick: (d) => ({ tournament: d }) },
  { key: "nexus",      name: "nexus",             speed: "fast", tabs: null,
    url: "/nexus/status",                      pick: (d) => ({ nexus: d }) },

  { key: "pipeline",   name: "pipeline",          speed: "slow", tabs: null,
    url: "/researcher/lab/pipeline",           pick: (d) => ({ pipeline: d }) },
  { key: "deployBoard", name: "deployment",       speed: "slow", tabs: null,
    url: "/researcher/lab/deployment-board",   pick: (d) => ({ deployBoard: d }) },
  { key: "quantumState", name: "quantum state",   speed: "slow", tabs: null,
    url: "/quantum/state",                     pick: (d) => ({ quantumState: d?.state || d }) },
  { key: "labDiscoveries", name: "discoveries",   speed: "slow", tabs: null,
    url: "/researcher/lab/discoveries?limit=12", pick: (d) => ({ labDiscoveries: d?.discoveries || [] }) },
  { key: "omega",      name: "omega",             speed: "slow", tabs: null,
    url: "/researcher/omega",                  pick: (d) => ({ omega: d }) },

  // ── feed-freshness sources: overview grid + their own tabs ─────────────────
  { key: "okxIntel",   name: "OKX",               speed: "slow", tabs: ["overview", "signals"],
    url: "/researcher/lab/okx",                pick: (d) => ({ okxIntel: d?.sources || null }) },
  { key: "weexIntel",  name: "WEEX",              speed: "slow", tabs: ["overview", "signals"],
    url: "/researcher/lab/weex",               pick: (d) => ({ weexIntel: d?.sources || null }) },
  { key: "onchain",    name: "on-chain",          speed: "slow", tabs: ["overview", "signals"],
    url: "/researcher/lab/onchain",            pick: (d) => ({ onchain: d?.sources || null }) },
  { key: "macro",      name: "macro",             speed: "slow", tabs: ["overview", "library"],
    url: "/researcher/lab/macro",              pick: (d) => ({ macro: d?.sources || null }) },
  { key: "research",   name: "research ingest",   speed: "slow", tabs: ["overview", "library"],
    url: "/researcher/lab/research?min_relevance=0&limit=80", pick: (d) => ({ research: d?.papers || [] }) },

  // ── pipeline tab ───────────────────────────────────────────────────────────
  { key: "governance", name: "governance",        speed: "slow", tabs: ["pipeline"],
    url: "/researcher/lab/governance",         pick: (d) => ({ governance: d }) },
  { key: "regimeMatrix", name: "regime matrix",   speed: "slow", tabs: ["pipeline"],
    url: "/researcher/lab/regime-matrix",      pick: (d) => ({ regimeMatrix: d }) },
  { key: "lessons",    name: "lessons",           speed: "slow", tabs: ["pipeline"],
    url: "/researcher/lab/lessons",            pick: (d) => ({ lessons: d }) },
  { key: "labRequests", name: "council requests", speed: "slow", tabs: ["pipeline"],
    url: "/researcher/lab/requests",           pick: (d) => ({ labRequests: d?.requests || [] }) },

  // ── discovery tab ──────────────────────────────────────────────────────────
  { key: "labBoard",   name: "leaderboard",       speed: "slow", tabs: ["discovery"],
    url: "/researcher/lab/leaderboard?limit=12", pick: (d) => ({ labBoard: d?.strategies || [] }) },
  { key: "promotions", name: "promotions",        speed: "slow", tabs: ["overview", "discovery"],
    url: "/researcher/quantum/tournament/promotions", pick: (d) => ({ promotions: d?.promotions || [] }) },
  { key: "modelLane",  name: "models",            speed: "slow", tabs: ["discovery"],
    url: "/researcher/lab/models",             pick: (d) => ({ modelLane: d?.configs || [] }) },
  { key: "strategies", name: "strategies",        speed: "slow", tabs: ["discovery"],
    url: "/researcher/strategies",             pick: (d) => ({ strategies: d?.strategies || [] }) },
  { key: "labForward", name: "forward-test",       speed: "slow", tabs: ["discovery"],
    url: "/researcher/lab/forward-trading",    pick: (d) => ({ labForward: d || {} }) },

  // ── desk tab ───────────────────────────────────────────────────────────────
  { key: "deskFindings", name: "findings",        speed: "slow", tabs: ["desk"],
    url: "/researcher/desk/findings?limit=24", pick: (d) => ({ deskFindings: d?.findings || [] }) },
  { key: "deskTheories", name: "theories",        speed: "slow", tabs: ["desk"],
    url: "/researcher/desk/theories?limit=12", pick: (d) => ({ deskTheories: d?.theories || [] }) },
  { key: "deskAgenda", name: "agenda",            speed: "slow", tabs: ["desk"],
    url: "/researcher/desk/agenda?limit=5",    pick: (d) => ({ deskAgenda: d?.agenda || [] }) },
  { key: "deskSandbox", name: "sandbox",          speed: "slow", tabs: ["desk"],
    url: "/researcher/desk/sandbox",           pick: (d) => ({ deskSandbox: d }) },
  { key: "deskActions", name: "actions",          speed: "slow", tabs: ["desk"],
    url: "/researcher/desk/actions?limit=20",  pick: (d) => ({ deskActions: d?.actions || [] }) },

  // ── signals tab ────────────────────────────────────────────────────────────
  { key: "hurst",      name: "hurst",             speed: "slow", tabs: ["signals"],
    url: "/researcher/hurst",                  pick: (d) => ({ hurst: d?.symbols || [] }) },
  { key: "ensemble",   name: "ensemble",          speed: "slow", tabs: ["signals"],
    url: "/researcher/ensemble",               pick: (d) => ({ ensemble: d?.consensus || [] }) },
  { key: "tcn",        name: "TCN votes",         speed: "slow", tabs: ["signals"],
    url: "/researcher/tcn_votes",
    pick: (d) => ({ tcn: d ? { ...(d.votes?.["BTC/USDT"] || {}), live: d.live } : null }) },
  { key: "calibration", name: "calibration",      speed: "slow", tabs: ["signals"],
    url: "/researcher/calibration",
    pick: (d) => ({ calibration: d?.deciles || [], totalTrades: d?.total_trades || 0,
                    overallWinRate: d?.overall_win_rate || 0 }) },

  // ── library tab ────────────────────────────────────────────────────────────
  { key: "papers",     name: "papers",            speed: "slow", tabs: ["library"],
    url: "/researcher/papers?limit=12",        pick: (d) => ({ papers: d?.papers || [] }) },

  // ── system tab ─────────────────────────────────────────────────────────────
  { key: "fixes",      name: "omega fixes",       speed: "slow", tabs: ["system"],
    url: "/researcher/omega/fixes?limit=8",    pick: (d) => ({ fixes: d?.fixes || [] }) },
];

/** Connections needed to render `tab` at the given speed. */
const connectionsFor = (tab, speed) =>
  CONNECTIONS.filter((c) => c.speed === speed && (c.tabs === null || c.tabs.includes(tab)));

// ── Tab ⟷ URL hash ───────────────────────────────────────────────────────────
// The tab lived in local state, so a reload, the back button or a pasted link
// always dumped you back on Overview. `#research/<tab>` makes a view addressable.
const HASH_PREFIX = "#research/";

const readTabFromHash = () => {
  if (typeof window === "undefined") return "overview";
  const raw = (window.location.hash || "").trim();
  if (!raw.startsWith(HASH_PREFIX)) return "overview";
  const id = raw.slice(HASH_PREFIX.length);
  return TABS.some((t) => t.id === id) ? id : "overview";
};

const waitFor = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getResearchConnection = async (url) => {
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      return await axios.get(url, { timeout: 15000 });
    } catch (error) {
      const status = error?.response?.status;
      const retryable = status === 429 || status === 503;
      if (!retryable || attempt === 2) throw error;
      await waitFor(900 * (attempt + 1) + Math.round(Math.random() * 250));
    }
  }
  throw new Error(`Research connection failed: ${url}`);
};

/**
 * Fetch a set of connections in polite batches and fold the successes into one
 * patch for `data`. Failures are reported by name rather than blanking the
 * panel, so a feed outage reads as an outage instead of as "no activity".
 */
const settleResearchConnections = async (conns, { stagger = true } = {}) => {
  if (!conns.length) return { patch: {}, failedNames: [], total: 0 };
  if (stagger) await waitFor(RESEARCH_INITIAL_DELAY_MS);
  const results = [];
  for (let start = 0; start < conns.length; start += RESEARCH_BATCH_SIZE) {
    const batch = conns.slice(start, start + RESEARCH_BATCH_SIZE);
    results.push(...await Promise.allSettled(
      batch.map((c) => getResearchConnection(`${API}${c.url}`))));
    if (start + RESEARCH_BATCH_SIZE < conns.length) await waitFor(RESEARCH_BATCH_GAP_MS);
  }
  const patch = {};
  const failedNames = [];
  results.forEach((r, i) => {
    const conn = conns[i];
    if (r.status === "fulfilled") {
      try { Object.assign(patch, conn.pick(r.value.data)); }
      catch { failedNames.push(conn.name); }
    } else {
      failedNames.push(conn.name);
    }
  });
  return { patch, failedNames, total: conns.length };
};

export default function ResearcherPage() {
  const [data, setData] = useState({
    omega: null, fixes: [], papers: [], strategies: [],
    hurst: [], ensemble: [], tcn: null,
    calibration: [], totalTrades: 0, overallWinRate: 0,
    nexus: null, loading: true,
    lab: null, labBoard: [], labDiscoveries: [],
    desk: null, deskFindings: [], deskTheories: [], deskAgenda: [], deskSandbox: null, deskActions: [],
    pipeline: null, governance: null, regimeMatrix: null, lessons: null, deployBoard: null, labRequests: [],
    research: [], macro: null,
    connections: null,
  });
  const [tab, setTab] = useState(readTabFromHash);
  const [resMin, setResMin] = useState(50);      // Research panel: min relevance filter
  const [resTheme, setResTheme] = useState(null); // Research panel: theme filter
  const [labRunning, setLabRunning] = useState(false);
  const [deskRunning, setDeskRunning] = useState(false);
  const [detail, setDetail] = useState(null);   // clicked research item → detail modal
  const [briefPaper, setBriefPaper] = useState(null); // paper → AEON implementation brief
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const refresh = useCallback(() => {
    setRefreshing(true);
    setRefreshNonce((n) => n + 1);
  }, []);

  const runLabCycle = useCallback(async () => {
    setLabRunning(true);
    try { await axios.post(`${API}/researcher/lab/run`); refresh(); } catch { /* ignore */ }
    setLabRunning(false);
  }, [refresh]);

  const runDeskCycle = useCallback(async () => {
    setDeskRunning(true);
    try { await axios.post(`${API}/researcher/desk/run`); refresh(); } catch { /* ignore */ }
    setTimeout(() => setDeskRunning(false), 4000);
  }, [refresh]);

  const toggleDesk = useCallback(async () => {
    try {
      const cur = (await axios.get(`${API}/researcher/desk/status`)).data?.enabled;
      await axios.post(`${API}/researcher/desk/toggle?on=${!cur}`);
      refresh();
    } catch { /* ignore */ }
  }, [refresh]);

  const toggleAct = useCallback(async () => {
    try {
      const cur = (await axios.get(`${API}/researcher/desk/status`)).data?.act?.act_enabled;
      if (!cur && !window.confirm("Enable ACT mode? The desk will AUTO-EXECUTE bounded, reversible actions on the live system (pause/resume engines, gates, risk/leverage within ceilings, canary deploys). You can disable anytime.")) return;
      await axios.post(`${API}/researcher/desk/act_toggle?on=${!cur}`);
      refresh();
    } catch { /* ignore */ }
  }, [refresh]);

  const actItem = useCallback(async (id, op) => {
    try { await axios.post(`${API}/researcher/desk/action/${id}/${op}`); refresh(); } catch { /* ignore */ }
  }, [refresh]);

  // ── Tab ⟷ hash ─────────────────────────────────────────────────────────────
  // Writing the hash makes the view addressable; listening for hashchange makes
  // the browser's back button move between tabs the way it looks like it should.
  const goTab = useCallback((id) => {
    setTab(id);
    if (typeof window !== "undefined" && readTabFromHash() !== id) {
      window.location.hash = `${HASH_PREFIX}${id}`;
    }
  }, []);

  useEffect(() => {
    const onHash = () => setTab(readTabFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // ── Data loading ───────────────────────────────────────────────────────────
  // Two beats instead of one. The fast set (4 connections backing the header)
  // refreshes every 20s; the slow set for the active tab every 5 min. Switching
  // tabs pulls that tab's slow set immediately.
  const applyResult = useCallback(({ patch, failedNames, total }, speed, alive) => {
    if (!alive()) return;
    setData((d) => {
      const next = { ...d, ...patch, loading: false };
      // The lab pill wants regime/ADX off the quantum state, which is its own
      // connection — merge it in here rather than coupling the two fetches.
      const qs = next.quantumState;
      if (next.lab && qs) {
        next.lab = { ...next.lab, regime: qs.regime, adx: qs.adx,
                     position_multiplier: qs.position_multiplier };
      }
      // Each beat reports only on the connections it asked for, so merge the two
      // beats' verdicts: keep the other beat's failures, replace this one's.
      const asked = new Set(CONNECTIONS.filter((c) => c.speed === speed).map((c) => c.name));
      const others = (d.connections?.failedNames || []).filter((n) => !asked.has(n));
      const names = Array.from(new Set([...others, ...failedNames])).sort();
      const totalAll = (d.connections?.totalBySpeed?.[speed] === undefined
        ? { ...(d.connections?.totalBySpeed || {}), [speed]: total }
        : { ...d.connections.totalBySpeed, [speed]: total });
      const grand = Object.values(totalAll).reduce((a, b) => a + b, 0);
      next.connections = {
        total: grand, ok: grand - names.length, failed: names.length,
        failedNames: names, checkedAt: new Date().toISOString(),
        totalBySpeed: totalAll,
      };
      return next;
    });
  }, []);

  // Fast beat — header vitals only.
  useEffect(() => {
    let mounted = true;
    const alive = () => mounted;
    const tick = async (stagger) => {
      try {
        const res = await settleResearchConnections(connectionsFor(tab, "fast"), { stagger });
        applyResult(res, "fast", alive);
      } catch { /* keep the last good values on screen */ }
    };
    tick(true);
    const t = setInterval(() => tick(false), RESEARCH_FAST_MS);
    return () => { mounted = false; clearInterval(t); };
    // `tab` is deliberately absent: the fast set is identical on every tab, so
    // re-subscribing on tab change would restart the beat for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshNonce, applyResult]);

  // Slow beat — only what the active tab actually renders.
  useEffect(() => {
    let mounted = true;
    const alive = () => mounted;
    const tick = async (stagger) => {
      try {
        const res = await settleResearchConnections(connectionsFor(tab, "slow"), { stagger });
        applyResult(res, "slow", alive);
      } catch { /* keep the last good values on screen */ }
      finally { if (mounted) setRefreshing(false); }
    };
    tick(true);
    const t = setInterval(() => tick(false), RESEARCH_SLOW_MS);
    return () => { mounted = false; clearInterval(t); };
  }, [tab, refreshNonce, applyResult]);


  if (data.loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 48, gap: 12, color: "rgba(255,255,255,0.3)" }}>
        <RefreshCw style={{ width: 18, height: 18, animation: "spin 1s linear infinite" }} />
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontFamily: "var(--font-mono)" }}>LOADING RESEARCHER</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 research-workspace">
      {detail && <ResearchDetailModal item={detail} onClose={() => setDetail(null)} />}
      {briefPaper && <PaperBriefModal paper={briefPaper} onClose={() => setBriefPaper(null)} />}
      {/* Sticky masthead — identity, live vitals, navigation and tab purpose.
          One rail instead of three stacked bands. */}
      <CommandHeader
        lab={data.lab} desk={data.desk} tournament={data.tournament}
        omega={data.omega} nexus={data.nexus}
        updatedAt={_agoLabel(data.connections?.checkedAt)}
        connections={data.connections}
        onRefresh={refresh}
        refreshing={refreshing}
        tab={tab}
        onTab={goTab}
      />

      {/* Tab content */}
      {tab === "overview" && <OverviewDeck data={data} onGo={goTab} />}

      {tab === "pipeline" && (
        <div className="space-y-3">
          <div className="rlab-intro-banner">
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>
              Every candidate's journey, one board: idea → backtest pressure → live monitor → promoted (or restricted).
              Numbers trace to a real backtest or paper fill — anything unknown shows "insufficient data".
            </div>
          </div>
          <CouncilLoopStrip requests={data.labRequests} />
          <PipelineBoard pipeline={data.pipeline} onOpen={setDetail} />
          <div className="rlab-cols">
            <Section title="Governance Chamber" sub="Where trust gets assigned" icon={Shield} color="#4dffaf" defaultOpen>
              <GovernanceChamber governance={data.governance} />
            </Section>
            <Section title="Deployment Board" sub="Production bridge · real-money reality" icon={Zap} color="#fbbf24" defaultOpen>
              <DeploymentBoard board={data.deployBoard} />
            </Section>
          </div>
          <Section title="Regime Matrix" sub="Engine × regime edge" icon={Activity} color="#5cf2ff">
            <RegimeMatrix matrix={data.regimeMatrix} />
          </Section>
          <Section title="Lessons Vault" sub="What didn't work — so we learn" icon={AlertTriangle} color="#fb923c">
            <LessonsVault lessons={data.lessons} />
          </Section>
        </div>
      )}

      {tab === "discovery" && (
        <div className="space-y-3">
          <div className="rlab-intro-banner rlab-note-rich">
            <div className="rlab-note-title">
              How AEON discovers a trading strategy — the pipeline, top to bottom
            </div>
            <div className="rlab-note-steps">
              <div><b style={{ color: "#4dffaf" }}>① Strategy Lab</b> &nbsp;invents & backtests thousands of ideas on past price data, then ranks the best.</div>
              <div><b style={{ color: "#c4b5fd" }}>② Tournament</b> &nbsp;the promising ideas compete <i>live</i> against simple baselines, after real fees.</div>
              <div><b style={{ color: "#f5b042" }}>③ Research Trading</b> &nbsp;survivors trade <i>forward on live prices</i> in a separate book — the truth test: does the backtest actually hold up?</div>
            </div>
            <div className="rlab-note-foot">
              Each stage below matches a step. The lower sections (models, wired strategies) are supporting detail — expand if you want them.
            </div>
          </div>
          <LabDiscoveryPanel
            lab={data.lab}
            leaderboard={data.labBoard}
            discoveries={data.labDiscoveries}
            onRun={runLabCycle}
            running={labRunning}
            onOpen={setDetail}
          />
          <Section title="② Tournament" sub="Promising ideas compete live vs. simple baselines, after real fees" icon={Trophy} color="#c4b5fd" defaultOpen>
            <TournamentPanel t={data.tournament} promotions={data.promotions} onOpen={setDetail} />
          </Section>
          <Section title="③ Research Trading" sub="Survivors trade forward on live prices (separate book) — does the backtest hold up?" icon={BarChart3} color="#f5b042" count={data.labForward?.strategies?.length} defaultOpen>
            <ForwardTradingPanel data={data.labForward} />
          </Section>

          <div className="rlab-section-lbl" style={{ marginTop: 6, opacity: 0.6 }}>Supporting detail — expand if you want it</div>
          <div className="rlab-cols">
            <Section title="Model Lane" sub="The self-tuning ML models the lab also runs" icon={Cpu} color="#7dd3fc">
              <ModelLanePanel configs={data.modelLane} />
            </Section>
            <Section title="Leaderboard, Visualized" sub="Chart: backtest champion vs. its live reality" icon={BarChart3} color="#4dffaf">
              <LabVisualPanel lab={data.lab} leaderboard={data.labBoard} onOpen={setDetail} />
            </Section>
          </div>
          <Section title="Wired Strategies" sub="Strategies already coded into the live trading engine" icon={Cpu} color="#c4b5fd" count={data.strategies?.length}>
            <StrategiesPanel strategies={data.strategies} onOpen={setDetail} />
          </Section>
        </div>
      )}

      {tab === "desk" && (
        <div className="space-y-3">
          <div className="rlab-intro-banner">
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>
              A self-driving quant org: specialists form theories, test them, keep what survives, and forward-test
              the best. A Head of Research sets the agenda; you hold the kill-switch on ACT mode.
            </div>
          </div>
          <DeskPanel
            desk={data.desk}
            findings={data.deskFindings}
            theories={data.deskTheories}
            agenda={data.deskAgenda}
            sandbox={data.deskSandbox}
            actions={data.deskActions}
            onRun={runDeskCycle}
            onToggle={toggleDesk}
            onActToggle={toggleAct}
            onActItem={actItem}
            onOpen={setDetail}
            running={deskRunning}
          />
          <Section title="Live Agent Feed" sub="Everything the research org is doing, in order" icon={Activity} color="#7dd3fc">
            <AgentFeedPanel />
          </Section>
        </div>
      )}

      {tab === "signals" && (
        <div className="space-y-3">
          <div className="rlab-intro-banner">
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>
              Live market intelligence feeding the research org: AEON's own regime reads, plus hourly exchange
              and on-chain ingest. These are inputs to strategies — not signals to trade directly.
            </div>
          </div>
          <LiveSignalsPanel hurst={data.hurst} ensemble={data.ensemble} tcn={data.tcn} />
          <div className="rlab-cols">
            <OkxIntelPanel sources={data.okxIntel} />
            <WeexIntelPanel sources={data.weexIntel} />
          </div>
          <div className="rlab-cols">
            <OnchainPanel sources={data.onchain} />
            <CalibrationPanel calibration={data.calibration} totalTrades={data.totalTrades} overallWinRate={data.overallWinRate} />
          </div>
        </div>
      )}

      {tab === "library" && (
        <div className="space-y-3">
          <div className="rlab-intro-banner">
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>
              Source material the lab reads: AEON's own research paper, plus arXiv q-fin and DefiLlama macro
              ingest, each scored for relevance by a local model. Open the source directly or request a structured AEON implementation brief from any paper card.
            </div>
          </div>
          <PapersPanel papers={data.papers} onBrief={setBriefPaper} />
          <Section title="Research Ingest" sub="arXiv q-fin · DefiLlama macro · local-model scored" icon={FileText} color="#93c5fd" defaultOpen>
            <ResearchIngestPanel papers={data.research} macro={data.macro}
              min={resMin} setMin={setResMin} theme={resTheme} setTheme={setResTheme}
              onBrief={setBriefPaper} />
          </Section>
        </div>
      )}

      {tab === "system" && (
        <div className="space-y-3">
          <div className="rlab-intro-banner">
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>
              Infrastructure under the research org: the NEXUS nervous system's regime state, and OMEGA's
              self-healing fix log.
            </div>
          </div>
          <NexusPanel nexus={data.nexus} />
          <OmegaPanel omega={data.omega} fixes={data.fixes} />
        </div>
      )}
    </div>
  );
}
